import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import toast from 'react-hot-toast';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import EventLoadingState from '../Common/EventLoadingState';
import PhoneInput from '../../Common/PhoneInput';
import SearchableSelect from '../../Common/SearchableSelect';
import AsyncSearchableSelect from '../../Common/AsyncSearchableSelect';
import AddClientModal from '../Common/AddClientModal';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getApplicationFormByToken,
  getCoordinatorApplicationFormByToken,
  previewApplicationFormByToken,
  getPreviewApplicationFormExternalCompanies,
  getPreviewApplicationFormCompanyClients,
  submitPublicApplicationForm,
  coordinatorSubmitApplicationForm,
  saveCoordinatorApplicationDraft,
  updateCoordinatorApplicationDraft,
  getCoordinatorApplicationDraft,
  getCoordinatorApplicationSubmission,
  updateCoordinatorApplicationSubmission,
  getEventApplicationFormSubmissionForEdit,
  getEventApplicationFormSubmissionDetail,
  updateEventApplicationFormSubmission,
  downloadPublicSubmissionPdf,
  downloadSubmissionPdf,
  downloadCoordinatorSubmissionPdf,
  emailCoordinatorSubmissionPdf,
  listPublicSubmissionPdfPacks,
  listSubmissionPdfPacks,
  listCoordinatorSubmissionPdfPacks,
  getPublicEventSettings,
  getPublicApplicationFormExternalCompanies,
  getPublicApplicationFormCompanyClients,
  getCoordinatorMyExternalCompany,
  getCoordinatorMyCompanyClients,
  getStaffApplicationFormByToken,
  getStaffApplicationFormExternalCompanies,
  getStaffApplicationFormCompanyClients,
  staffSubmitApplicationFormOnBehalf,
  validateFormPromoCode,
  getApplicationFormCompanyVolumeStats,
  getCoordinatorApplicationFormVolumeStats,
} from '../../../services/eventApi';
import fileApi from '../../../services/fileApi';
import { downloadSubmissionPdfPacks } from '../../../utils/downloadSubmissionPdfPacks';
import { formatEventDisplayTitle } from '../../../utils/eventDisplayTitle';
import {
  DEFAULT_PDF_PACK_KEY,
  packTitleForKey,
  resolvePdfOutputs,
} from '../../../utils/applicationFormPdfPacks';
import { taxConfigApi } from '../../../services/financeApi';
import SubmissionVersionHistory from './SubmissionVersionHistory';
import SubmissionSignedDocumentPanel from './SubmissionSignedDocumentPanel';
import ApplicationQuestionField, { isQuestionValueEmpty } from './ApplicationQuestionField';
import { isUploadQuestionType, coerceAnswerValueForQuestion } from '../../../utils/applicationAnswerCoercion';
import ConfirmationModal from '../Common/EventConfirmationModal';
import { isNonAnswerContentType } from '../../../utils/applicationQuestionTypes';
import { isQuestionVisible, percentageRangeError } from '../../../utils/applicationQuestionValidation';
import { isPurePrintDeclaration } from '../../../utils/applicationQuestionTypeConfig';
import { peoplePluralLower, peopleSingularLower, personRowHeading, resolvePeopleLabels } from '../../../utils/applicationPeopleLabels';
import { eventAllowsIndustryLinking } from '../../../utils/eventIndustryLinking';
import { coordinatorNeedsClientSelection, resolveCoordinatorSettings, coordinatorRequiresClientSelection } from '../../../utils/applicationCoordinatorSettings';
import {
  buildPricingBaselineFromSubmission,
  resolveSubmissionEditPolicy,
  submissionPricingWillChange,
} from '../../../utils/submissionEditPolicy';
import { buildApplicationFormQuestionMap } from '../../../utils/dataGridAnswerDisplay';
import { getProformaCancelEditMessage } from '../../../utils/eventSubmissionDisplay';
import {
  SETUP_PAGE_ID,
  buildPublicFormSteps,
  isPublicFormPaginationEnabled,
  sectionsForPublicFormStep,
  findEntryDetailsPage,
  isEntryDetailsPage,
} from '../../../utils/applicationFormPages';
import { resolveCategoryQuestionsSectionKey } from '../../../utils/applicationFormCategoryQuestions';
import FormPageStepper from './FormPageStepper';
import UploadProgressOverlay from './UploadProgressOverlay';
import { formatMarketMoney, marketLabel, normalizePricingMarket } from '../../../utils/eventPricingDisplay';
import { computeEventTaxBreakdown } from '../../../utils/crmTaxMath';
import { resolveApplicableBulkDiscountTier } from '../../../utils/eventBulkDiscount';

function normalizeCategoryIdRef(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && value._id) return String(value._id);
  return String(value);
}

function skipsAnswerCollection(question) {
  return isNonAnswerContentType(question?.type) || isPurePrintDeclaration(question);
}

/** Count how many new (pending, not yet uploaded) files exist in a question answer value. */
function countNewFilesInAnswer(question, value) {
  if (!value) return 0;
  if (question?.type === 'TEXT_WITH_FILE') {
    const rows = Array.isArray(value?.files) ? value.files : [];
    return rows.filter((f) => f && typeof f === 'object' && !f.fileId && typeof f !== 'string').length;
  }
  if (isUploadQuestionType(question)) {
    const rows = Array.isArray(value) ? value : [value];
    return rows.filter((f) => f && typeof f === 'object' && !f.fileId && typeof f !== 'string').length;
  }
  return 0;
}

function resolveApplicableBulkTier(tiers, count) {
  return resolveApplicableBulkDiscountTier(tiers, count);
}

function resolveEarlyBird(event, now = new Date()) {
  if (!event?.earlyBirdDiscountEnabled) return null;
  const cutoff = event.earlyBirdDiscountCutoffDate || event.earlyBirdDeadline;
  if (!cutoff) return null;
  const pct = Number(event.earlyBirdDiscountPercent || 0);
  if (pct <= 0) return null;
  if (now > new Date(cutoff)) return null;
  return {
    discountPercent: pct,
    description: event.earlyBirdDiscountDescription || `Early bird discount (${pct}%)`,
  };
}

const emptyPerson = () => ({ fullName: '', designation: '', email: '', phone: '', categoryId: '', selectedIndustryId: '', categoryPath: [], answers: {} });

function normalizeSubmissionRefId(value) {
  if (!value) return '';
  if (typeof value === 'object' && value._id) return String(value._id);
  return String(value);
}

/** Root categories for an industry (those whose parent is not in the eligible list). */
function getRootCategoriesForIndustry(industryId, allCats) {
  const ind = String(industryId || '');
  if (!ind) return [];
  const indCatIds = new Set(allCats.filter((c) => String(c.industryId) === ind).map((c) => String(c.id)));
  return allCats
    .filter((c) => String(c.industryId) === ind && (!c.parentCategoryId || !indCatIds.has(String(c.parentCategoryId))))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** Root categories when the event uses categories only (no industry layer). */
function getRootCategoriesCategoryOnly(allCats) {
  const catIds = new Set(allCats.map((c) => String(c.id)));
  return allCats
    .filter((c) => !c.parentCategoryId || !catIds.has(String(c.parentCategoryId)))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** Direct children of a given category within the eligible list. */
function getChildCategoriesForParent(parentId, allCats) {
  const pid = String(parentId || '');
  if (!pid) return [];
  return allCats
    .filter((c) => c.parentCategoryId && String(c.parentCategoryId) === pid)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** Reconstruct the drill-down path from a leaf category id. */
function buildCategoryPath(categoryId, allCats) {
  const byId = new Map(allCats.map((c) => [String(c.id), c]));
  const path = [];
  let cur = byId.get(String(categoryId || ''));
  const seen = new Set();
  while (cur && !seen.has(String(cur.id))) {
    seen.add(String(cur.id));
    path.unshift(String(cur.id));
    const pid = cur.parentCategoryId ? String(cur.parentCategoryId) : '';
    cur = pid ? byId.get(pid) : null;
  }
  return path;
}

function allFormQuestionsForAnswers(formOrQuestions) {
  if (Array.isArray(formOrQuestions)) return formOrQuestions;
  return [
    ...(formOrQuestions?.questions || []),
    ...(formOrQuestions?.categoryQuestionSets || []).flatMap((cqs) => cqs?.questions || []),
  ];
}

// fieldKey is not persisted in AnswerSchema, so stored answers only have questionId.
// We build the map with both questionId and fieldKey entries so either lookup works.
function answersArrayToMap(answersArr, formOrQuestions) {
  const formQuestions = allFormQuestionsForAnswers(formOrQuestions);
  const qById = new Map();
  const qIdToFieldKey = new Map();
  for (const q of formQuestions) {
    if (q?.id != null) {
      qById.set(String(q.id), q);
      if (q.fieldKey) qIdToFieldKey.set(String(q.id), String(q.fieldKey));
    }
  }
  const map = {};
  for (const a of answersArr || []) {
    const qId = String(a.questionId || '');
    const q = qById.get(qId);
    let val = a.value;
    if (q) {
      val = coerceAnswerValueForQuestion(q, val);
    }
    const fk = a.fieldKey || qIdToFieldKey.get(qId) || '';
    if (qId) map[qId] = val;
    if (fk && fk !== qId) map[fk] = val;
  }
  for (const q of formQuestions) {
    const id = String(q?.id || '');
    const fk = String(q?.fieldKey || '').trim();
    if (!id || !fk) continue;
    if (map[id] !== undefined && map[fk] === undefined) map[fk] = map[id];
    if (map[fk] !== undefined && map[id] === undefined) map[id] = map[fk];
  }
  return map;
}

function peopleFromSubmission(peopleArr, form, submissionCategoryId = '') {
  if (!Array.isArray(peopleArr) || !peopleArr.length) return [emptyPerson()];
  const fallbackCategoryId = submissionCategoryId ? String(submissionCategoryId) : '';
  return peopleArr.map((p) => {
    const personCategoryId = p.categoryId ? String(p.categoryId) : fallbackCategoryId;
    return {
      fullName: p.fullName || '',
      designation: p.designation || '',
      email: p.email || '',
      phone: p.phone || '',
      categoryId: personCategoryId,
      selectedIndustryId: '',
      categoryPath: [],
      answers: answersArrayToMap(p.answers, form),
    };
  });
}

/** Flatten eligibility categories with industry context (server shape). */
function flattenEligibilityCategories(eligibilities) {
  return (eligibilities || []).flatMap((e) =>
    (e.categories || []).map((c) => ({
      ...c,
      industryId: e.industryId,
      industryName: e.industryName || '',
    }))
  );
}

/** Path: Industry → Parent → … (excludes the leaf itself), for subtitle under entry category. */
function ancestorPathExcludingLeaf(leaf, byId) {
  const parts = [];
  if (leaf?.industryName) parts.push(leaf.industryName);
  let pid = leaf?.parentCategoryId;
  const seen = new Set();
  const catAncestors = [];
  while (pid && !seen.has(String(pid))) {
    seen.add(String(pid));
    const p = byId.get(String(pid));
    if (!p) break;
    catAncestors.unshift(p.name || '');
    pid = p.parentCategoryId;
  }
  return [...parts, ...catAncestors].filter(Boolean).join(' → ');
}

function buildSubmissionMetaFromRecord(submission, loadedForm) {
  return {
    applicationNumber: submission.applicationNumber || '',
    status: submission.status || '',
    submittedAt: submission.createdAt || null,
    updatedAt: submission.updatedAt || null,
    industryName: submission.industryName || submission.industry?.name || '',
    categoryName: submission.categoryName || submission.category?.name || '',
    formName: submission.formName || loadedForm?.name || '',
    paymentReferenceNumber: submission.paymentReferenceNumber || '',
    reviewedAt: submission.reviewedAt || null,
    reviewedByName: submission.reviewedByName || '',
    reviewNote: submission.reviewNote || '',
    signedApplicationDocument: submission.signedApplicationDocument || null,
    signedPdfPacks: Array.isArray(submission.signedPdfPacks) ? submission.signedPdfPacks : [],
  };
}

function hydrateStaffSubmissionState(submission, loadedForm) {
  const submissionCategoryId = submission.category?._id || submission.category || submission.categoryId || '';
  const categoryIdStr = submissionCategoryId ? String(submissionCategoryId) : '';
  return {
    submissionCategoryId: categoryIdStr,
    commonAnswers: answersArrayToMap(submission.answers || [], loadedForm),
    people: peopleFromSubmission(submission.people, loadedForm, categoryIdStr),
    submissionMeta: buildSubmissionMetaFromRecord(submission, loadedForm),
  };
}

function portalApplyErrorMessage(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    const parts = data.errors
      .map((e) => String(e?.message || e?.msg || '').trim())
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  return data?.message || fallback;
}

const cleanCompanyName = (str) => String(str || '').replace(/\s*\((Company|Agency)\)\s*$/i, '').trim();

/**
 * @param {{ coordinatorPortal?: boolean, staffCreate?: boolean, staffEdit?: boolean, staffView?: boolean, readOnly?: boolean }} props
 */
const PublicApplicationForm = ({ coordinatorPortal = false, staffCreate = false, staffEdit = false, staffView = false, readOnly = false }) => {
  const { token, submissionId, eventId, draftId } = useParams();
  const navigate = useNavigate();
  const handleBackToSubmissions = useDetailBack(eventId ? `/events/${eventId}?tab=submissions` : '/events/events');
  const handleBackToPortal = useDetailBack('/events/external-portal');
  const portalDraftMode = Boolean(coordinatorPortal && draftId);
  const portalLikeEdit = coordinatorPortal || staffEdit || staffView;
  const staffSubmissionMode = staffEdit || staffView;
  const attachmentDeliveryMode = coordinatorPortal ? 'download' : 'open';
  const location = useLocation();
  const { pathname } = location;
  const isPreviewMode = pathname.startsWith('/events/application-forms/preview/');
  const { hasPermission, user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Upload progress overlay state
  const [uploadOverlay, setUploadOverlay] = useState({ visible: false, files: [], overallPct: 0, phase: 'uploading' });
  const [draftSaving, setDraftSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [form, setForm] = useState(null);
  const [eligibilities, setEligibilities] = useState([]);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [lastApplicationNumber, setLastApplicationNumber] = useState('');
  const [lastSubmissionId, setLastSubmissionId] = useState('');
  const [emailingPdf, setEmailingPdf] = useState(false);
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterMobile, setSubmitterMobile] = useState('');
  const [brandProduct, setBrandProduct] = useState({ brandsProducts: '', brandCategory: '', brandWebsite: '' });
  const [externalCompanyId, setExternalCompanyId] = useState('');
  const [externalCompanyLabel, setExternalCompanyLabel] = useState('');
  const [eventsApplyEnabled, setEventsApplyEnabled] = useState(true);
  const [externalCompanyClientId, setExternalCompanyClientId] = useState('');
  const [externalCompanyClientLabel, setExternalCompanyClientLabel] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [eventLogoSrc, setEventLogoSrc] = useState('');
  /** Category selection is done per nominee/person row. */
  const [commonAnswers, setCommonAnswers] = useState({});
  const [people, setPeople] = useState([emptyPerson()]);
  const [isStepChanging, setIsStepChanging] = useState(false);
  const [validationSections, setValidationSections] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({
    submitterName: false,
    submitterEmail: false,
    submitterMobile: false,
    externalCompanyId: false,
    externalCompanyClientId: false,
    industryId: false,
    categoryId: false,
    brandsProducts: false,
    people: [],
    commonAnswers: {},
  });
  const [eventTaxConfigs, setEventTaxConfigs] = useState([]);
  const [submissionMeta, setSubmissionMeta] = useState(null);
  const [legacyOnboardingRequiresIntent, setLegacyOnboardingRequiresIntent] = useState(false);
  const [allowsClosedRegistrationApply, setAllowsClosedRegistrationApply] = useState(false);
  const [legacySubmissionIntent, setLegacySubmissionIntent] = useState('');
  const [pricingBaseline, setPricingBaseline] = useState(null);
  const [editPolicy, setEditPolicy] = useState(null);
  const pricingFieldsLocked = Boolean(editPolicy?.pricingFieldsLocked);
  const lockPeopleStructure = Boolean(editPolicy?.lockPeopleStructure);
  const categoryFieldsReadOnly = readOnly || pricingFieldsLocked;
  const allowCompanyChange = Boolean(staffEdit && editPolicy?.allowCompanyChange && !readOnly);
  const [requireAgreement, setRequireAgreement] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [proformaCancelPromptOpen, setProformaCancelPromptOpen] = useState(false);
  const [financeAckPromptOpen, setFinanceAckPromptOpen] = useState(false);
  const [topLevelIndustryId, setTopLevelIndustryId] = useState('');
  const [topLevelCategoryPath, setTopLevelCategoryPath] = useState([]);
  const [topLevelCategoryId, setTopLevelCategoryId] = useState('');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [externalCompanyDiscountPercentage, setExternalCompanyDiscountPercentage] = useState(0);
  const [externalCompanyDiscountNotes, setExternalCompanyDiscountNotes] = useState('');
  const [qualifyingSubmissionCount, setQualifyingSubmissionCount] = useState(0);

  const allEligibilityCategories = useMemo(() => flattenEligibilityCategories(eligibilities), [eligibilities]);
  const pricingMarket = useMemo(() => normalizePricingMarket(form?.pricingMarket), [form?.pricingMarket]);
  const fmtMoney = useCallback((amount) => formatMarketMoney(amount, pricingMarket), [pricingMarket]);
  const peopleLabels = useMemo(() => resolvePeopleLabels(form?.settings), [form?.settings]);
  const peoplePluralLc = useMemo(() => peoplePluralLower(peopleLabels), [peopleLabels]);
  const peopleSingularLc = useMemo(() => peopleSingularLower(peopleLabels), [peopleLabels]);
  const requiresSignedDocUpload = useMemo(() => {
    if (form?.settings?.requiresSignedDocumentUpload !== undefined) {
      return Boolean(form.settings.requiresSignedDocumentUpload);
    }
    const eventName = String(form?.event?.name || '').toLowerCase();
    if (eventName.includes('brand excellence')) {
      return false;
    }
    return true;
  }, [form]);

  const pricingBreakdown = useMemo(() => {
    const cid = topLevelCategoryId;
    const cat = cid ? allEligibilityCategories.find((c) => String(c.id) === cid) : null;
    const count = people.length;
    const lines = [];
    let entryFeeTotal = 0;
    if (cat && count > 0) {
      const price = Number(cat.price || 0);
      const subtotal = price * count;
      lines.push({ id: cid, name: cat.name, price, count, subtotal });
      entryFeeTotal = subtotal;
    }

    const event = form?.event;
    const calculationBasis = String(event?.discountTaxCalculationBasis || 'PRE_TAX').toUpperCase();

    // 1. Determine active discount
    let discountInfo = null;

    if (appliedPromo) {
      discountInfo = {
        type: 'PROMO_CODE',
        code: appliedPromo.code,
        label: `Promo Code (${appliedPromo.code})`,
        discountType: appliedPromo.discountType,
        discountValue: Number(appliedPromo.discountValue || 0),
        description: appliedPromo.description || `Promo code ${appliedPromo.code}`,
      };
    } else {
      // Check automated eligible discounts
      const isExemptCategory = Boolean(cat?.excludeFromVolumeDiscount);
      let bulkDiscount = null;

      if (!isExemptCategory && event?.bulkDiscountsEnabled && Array.isArray(event?.bulkDiscountTiers) && count > 0) {
        const isRetroactive = String(event?.bulkDiscountMode || '').toUpperCase() === 'RETROACTIVE';
        const effectiveTotalCount = qualifyingSubmissionCount + count;
        const retroactiveTier = isRetroactive
          ? resolveApplicableBulkDiscountTier(event.bulkDiscountTiers, effectiveTotalCount)
          : null;

        const catPrice = Number(cat?.price || 0);
        let totalBulkTierAmount = 0;
        let matchedTierDescription = '';
        let hasApplicableTier = false;

        for (let i = 1; i <= count; i += 1) {
          const entrySeq = qualifyingSubmissionCount + i;
          const tier = isRetroactive
            ? retroactiveTier
            : resolveApplicableBulkDiscountTier(event.bulkDiscountTiers, entrySeq);
          if (tier) {
            hasApplicableTier = true;
            if (!matchedTierDescription) matchedTierDescription = tier.description;
            if (tier.discountType === 'FIXED_AMOUNT' || Number(tier.discountAmount) > 0) {
              totalBulkTierAmount += Number(tier.discountAmount);
            } else if (tier.discountType === 'FIXED_PRICE' || Number(tier.unitPrice) > 0) {
              const diff = Math.max(0, catPrice - Number(tier.unitPrice));
              totalBulkTierAmount += diff;
            } else if (Number(tier.discountPercent) > 0) {
              totalBulkTierAmount += Math.round(catPrice * (Number(tier.discountPercent) / 100) * 100) / 100;
            }
          }
        }

        if (hasApplicableTier && totalBulkTierAmount > 0) {
          const label = isRetroactive
            ? `Volume Discount (${effectiveTotalCount} entries: Rs. ${Number(totalBulkTierAmount).toLocaleString()} off)`
            : `Volume Discount (Entry #${effectiveTotalCount}: Rs. ${Number(totalBulkTierAmount).toLocaleString()} off)`;
          bulkDiscount = {
            type: 'BULK',
            label,
            discountType: 'FIXED',
            discountValue: totalBulkTierAmount,
            amount: totalBulkTierAmount,
            description: matchedTierDescription || `Volume discount for ${effectiveTotalCount} entries`,
          };
        }
      }

      const companyPct = Number(externalCompanyDiscountPercentage || 0);
      const earlyBird = resolveEarlyBird(event);
      const earlyBirdPct = earlyBird ? Number(earlyBird.discountPercent || 0) : 0;

      const bulkAmount = bulkDiscount ? bulkDiscount.amount : 0;
      const companyAmount = companyPct > 0 ? Math.round(entryFeeTotal * (companyPct / 100) * 100) / 100 : 0;
      const earlyBirdAmount = earlyBirdPct > 0 ? Math.round(entryFeeTotal * (earlyBirdPct / 100) * 100) / 100 : 0;

      const maxAmount = Math.max(bulkAmount, companyAmount, earlyBirdAmount);
      if (maxAmount > 0) {
        if (bulkAmount === maxAmount && bulkDiscount) {
          discountInfo = bulkDiscount;
        } else if (companyAmount === maxAmount) {
          discountInfo = {
            type: 'COMPANY',
            label: 'Company Discount',
            discountType: 'PERCENTAGE',
            discountValue: companyPct,
            description: externalCompanyDiscountNotes ? `${externalCompanyDiscountNotes} (${companyPct}%)` : `Company profile discount (${companyPct}%)`,
          };
        } else if (earlyBirdAmount === maxAmount) {
          discountInfo = {
            type: 'EARLY_BIRD',
            label: 'Early Bird Discount',
            discountType: 'PERCENTAGE',
            discountValue: earlyBirdPct,
            description: earlyBird?.description || `Early bird discount (${earlyBirdPct}%)`,
          };
        }
      }
    }

    let discountAmount = 0;
    let taxLines = [];
    let taxTotal = 0;
    let grandTotal = 0;

    if (entryFeeTotal > 0 && discountInfo) {
      if (calculationBasis === 'POST_TAX') {
        const fullTaxBreakdown = computeEventTaxBreakdown({
          amount: entryFeeTotal,
          componentKey: 'EVENT_ENTRY_FEE',
          taxes: eventTaxConfigs,
        });
        taxLines = (fullTaxBreakdown.taxLines || []).map((tax) => ({
          name: tax.name,
          code: tax.code || '',
          percentage: Number(tax.percentage || 0),
          amount: Number(tax.taxAmount || tax.amount || 0),
        }));
        taxTotal = Number(fullTaxBreakdown.exclusiveTaxAmount || fullTaxBreakdown.totalTaxAmount || 0);
        const totalBeforeDiscount = entryFeeTotal + taxTotal;

        if (discountInfo.discountType === 'FIXED') {
          discountAmount = Math.min(totalBeforeDiscount, Math.max(0, discountInfo.discountValue));
        } else {
          const pct = Math.min(100, Math.max(0, discountInfo.discountValue));
          discountAmount = Math.round(totalBeforeDiscount * (pct / 100) * 100) / 100;
        }
        grandTotal = Math.max(0, Math.round((totalBeforeDiscount - discountAmount) * 100) / 100);
      } else {
        // PRE_TAX (Category Price)
        if (discountInfo.discountType === 'FIXED') {
          discountAmount = Math.min(entryFeeTotal, Math.max(0, discountInfo.discountValue));
        } else {
          const pct = Math.min(100, Math.max(0, discountInfo.discountValue));
          discountAmount = Math.round(entryFeeTotal * (pct / 100) * 100) / 100;
        }
        const taxableBase = Math.max(0, Math.round((entryFeeTotal - discountAmount) * 100) / 100);

        const taxBreakdown = computeEventTaxBreakdown({
          amount: entryFeeTotal,
          componentKey: 'EVENT_ENTRY_FEE',
          taxes: eventTaxConfigs,
          discountAmount,
        });
        taxLines = (taxBreakdown.taxLines || []).map((tax) => ({
          name: tax.name,
          code: tax.code || '',
          percentage: Number(tax.percentage || 0),
          amount: Number(tax.taxAmount || tax.amount || 0),
        }));
        taxTotal = Number(taxBreakdown.exclusiveTaxAmount || taxBreakdown.totalTaxAmount || 0);
        grandTotal = Number(taxBreakdown.grandTotal || (taxableBase + taxTotal));
      }
    } else {
      const taxBreakdown = computeEventTaxBreakdown({
        amount: entryFeeTotal,
        componentKey: 'EVENT_ENTRY_FEE',
        taxes: eventTaxConfigs,
      });
      taxLines = (taxBreakdown.taxLines || []).map((tax) => ({
        name: tax.name,
        code: tax.code || '',
        percentage: Number(tax.percentage || 0),
        amount: Number(tax.taxAmount || tax.amount || 0),
      }));
      taxTotal = Number(taxBreakdown.exclusiveTaxAmount || taxBreakdown.totalTaxAmount || 0);
      grandTotal = Number(taxBreakdown.grandTotal || (entryFeeTotal + taxTotal));
    }

    return {
      lines,
      entryFeeTotal,
      rawEntryFeeTotal: entryFeeTotal,
      isCategoryVolumeExempt: Boolean(cat?.excludeFromVolumeDiscount),
      discountInfo,
      discountAmount,
      discountTaxCalculationBasis: calculationBasis,
      taxLines,
      taxTotal,
      grandTotal,
    };
  }, [
    topLevelCategoryId,
    people.length,
    allEligibilityCategories,
    eventTaxConfigs,
    form?.event,
    appliedPromo,
    qualifyingSubmissionCount,
    externalCompanyDiscountPercentage,
    externalCompanyDiscountNotes,
    peopleSingularLc,
    peoplePluralLc,
  ]);

  const handleApplyPromoCode = async () => {
    const code = String(promoCodeInput || '').trim();
    if (!code) {
      setPromoError('Please enter a promo code');
      return;
    }
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await validateFormPromoCode(token || form?.publicToken, {
        code,
        externalCompanyId: externalCompanyId || undefined,
        subTotal: pricingBreakdown.rawEntryFeeTotal || pricingBreakdown.entryFeeTotal,
      });
      const data = res.data;
      if (data?.valid && data?.promo) {
        setAppliedPromo(data.promo);
        setPromoCodeInput(data.promo.code || code);
        toast.success(data.message || `Promo code "${data.promo.code}" applied!`);
      } else {
        setPromoError(data?.message || 'Invalid promo code');
        setAppliedPromo(null);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to validate promo code';
      setPromoError(msg);
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setAppliedPromo(null);
    setPromoCodeInput('');
    setPromoError('');
    toast.success('Promo code removed');
  };

  const categoryById = useMemo(() => {
    const m = new Map();
    for (const c of allEligibilityCategories) {
      m.set(String(c.id), c);
    }
    return m;
  }, [allEligibilityCategories]);

  /** Unique industries from the eligibility list, sorted by name. */
  const uniqueIndustries = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const e of eligibilities) {
      if (!e.industryId || seen.has(String(e.industryId))) continue;
      seen.add(String(e.industryId));
      list.push({ id: String(e.industryId), name: e.industryName || '' });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibilities]);

  /** Category-only events: no industry picker; categories are selected directly. */
  const categoryOnlyIntake = !eventAllowsIndustryLinking(form?.event);

  const activeRootCategories = useMemo(() => {
    if (categoryOnlyIntake) return getRootCategoriesCategoryOnly(allEligibilityCategories);
    if (!topLevelIndustryId) return [];
    return getRootCategoriesForIndustry(topLevelIndustryId, allEligibilityCategories);
  }, [categoryOnlyIntake, topLevelIndustryId, allEligibilityCategories]);

  /** Submission categories that have no eligible children in this form (end / leaf nodes only). */
  const leafEntryOptions = useMemo(() => {
    const leaves = allEligibilityCategories.filter((c) => !c.hasEligibleChildren);
    const withPath = leaves.map((c) => ({
      ...c,
      pathBelowName: ancestorPathExcludingLeaf(c, categoryById),
    }));
    withPath.sort((a, b) => {
      const pa = `${a.pathBelowName} ${a.name}`.toLowerCase();
      const pb = `${b.pathBelowName} ${b.name}`.toLowerCase();
      return pa.localeCompare(pb);
    });
    return withPath;
  }, [allEligibilityCategories, categoryById]);

  /** When loading an existing submission, rebuild top-level industryId + categoryPath from the saved categoryId. */
  useEffect(() => {
    if (!allEligibilityCategories.length || !topLevelCategoryId) return;
    if (topLevelIndustryId && topLevelCategoryPath.length) return;
    const byId = new Map(allEligibilityCategories.map((c) => [String(c.id), c]));
    const leaf = byId.get(String(topLevelCategoryId));
    if (!leaf) return;
    setTopLevelIndustryId(String(leaf.industryId || ''));
    setTopLevelCategoryPath(buildCategoryPath(topLevelCategoryId, allEligibilityCategories));
  }, [allEligibilityCategories, topLevelCategoryId]);

  /** Keep nominee rows aligned with the selected entry category (single top-level category flow). */
  useEffect(() => {
    if (!topLevelCategoryId) return;
    setPeople((prev) => {
      const needsSync = prev.some((p) => String(p.categoryId || '') !== String(topLevelCategoryId));
      if (!needsSync) return prev;
      return prev.map((p) => ({ ...p, categoryId: String(topLevelCategoryId) }));
    });
  }, [topLevelCategoryId]);

  useEffect(() => {
    if (!coordinatorPortal) return;
    if (!hasPermission('EVENTS:EXTERNAL_PORTAL:ACCESS')) {
      setError('You need company portal access to use this page.');
    }
  }, [coordinatorPortal, hasPermission]);

  useEffect(() => {
    if (!staffEdit) return;
    if (!hasPermission('EVENTS:APPLICATIONFORMS:UPDATE') && !isAdmin()) {
      setError('You do not have permission to edit application submissions.');
    }
  }, [staffEdit, hasPermission, isAdmin]);

  useEffect(() => {
    if (!staffView) return;
    if (!hasPermission('EVENTS:APPLICATIONFORMS:READ')
      && !hasPermission('EVENTS:SUBMISSIONS:READ')
      && !hasPermission('EVENTS:SCREENING_PHASE_1:READ')
      && !hasPermission('EVENTS:SCREENING_PHASE_2:READ')
      && !isAdmin()) {
      setError('You do not have permission to view application submissions.');
    }
  }, [staffView, hasPermission, isAdmin]);

  useEffect(() => {
    if (!staffCreate) return;
    if (!hasPermission('EVENTS:SUBMISSIONS:CREATE') && !isAdmin()) {
      setError('You do not have permission to submit applications on behalf of external companies.');
    }
  }, [staffCreate, hasPermission, isAdmin]);

  useEffect(() => {
    if (!coordinatorPortal || submissionId || draftId || !user) return;
    const fullName = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ') || user.name || '';
    if (fullName) setSubmitterName(fullName);
    if (user.email) setSubmitterEmail(user.email);
    if (user.profile?.phone) setSubmitterMobile(user.profile.phone);
  }, [coordinatorPortal, submissionId, user]);

  useEffect(() => {
    if (!coordinatorPortal || submissionId || draftId) return;
    (async () => {
      try {
        const res = await getCoordinatorMyExternalCompany();
        const company = res.data?.data;
        if (!company?._id) return;
        setExternalCompanyId(String(company._id));
        setExternalCompanyLabel(company.label || company.companyName || '');
        setExternalCompanyDiscountPercentage(Number(company.discountPercentage || 0));
        setExternalCompanyDiscountNotes(String(company.discountNotes || ''));
        const enabled = company.eventsEnabled !== false;
        setEventsApplyEnabled(enabled);
        if (!enabled) {
          setError('Your company or agency is not permitted to submit new event applications. Please contact the event organiser.');
        }
      } catch (_) {
        /* non-blocking — submit will fail with clear server message */
      }
    })();
  }, [coordinatorPortal, submissionId]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');

        if (staffView && eventId && submissionId) {
          const res = await getEventApplicationFormSubmissionDetail(eventId, submissionId);
          const bundle = res.data?.data;
          const loadedForm = bundle?.form;
          const submission = bundle?.submission;
          if (!loadedForm || !submission) {
            setError('Submission not found');
            return;
          }
          setForm(loadedForm);
          setEligibilities(Array.isArray(loadedForm?.eligibilities) ? loadedForm.eligibilities : []);
          setSubmitterName(submission.submitterName || '');
          setSubmitterEmail(submission.submitterEmail || '');
          setSubmitterMobile(submission.submitterMobile || '');
          setBrandProduct({
            brandsProducts: submission.brandProduct?.brandsProducts || '',
            brandCategory: submission.brandProduct?.brandCategory || '',
            brandWebsite: submission.brandProduct?.brandWebsite || '',
          });
          setExternalCompanyId(normalizeSubmissionRefId(submission.externalCompanyId));
          setExternalCompanyLabel(submission.externalCompanyName || '');
          setExternalCompanyClientId(normalizeSubmissionRefId(submission.externalCompanyClientId));
          setExternalCompanyClientLabel(submission.externalCompanyClientName || '');
          if (bundle?.companyVolumeStats?.qualifyingSubmissionCount != null) {
            setQualifyingSubmissionCount(bundle.companyVolumeStats.qualifyingSubmissionCount);
          }
          const hydrated = hydrateStaffSubmissionState(submission, loadedForm);
          setSubmissionMeta(hydrated.submissionMeta);
          setPricingBaseline(buildPricingBaselineFromSubmission(submission));
          setCommonAnswers(hydrated.commonAnswers);
          setPeople(hydrated.people);
          if (hydrated.submissionCategoryId) setTopLevelCategoryId(hydrated.submissionCategoryId);
          if (submission.industry?._id || submission.industry) {
            setTopLevelIndustryId(String(submission.industry?._id || submission.industry));
          }
          return;
        }

        if (staffEdit && eventId && submissionId) {
          const res = await getEventApplicationFormSubmissionForEdit(eventId, submissionId);
          const bundle = res.data?.data;
          const loadedForm = bundle?.form;
          const submission = bundle?.submission;
          if (!loadedForm || !submission) {
            setError('Submission not found');
            return;
          }
          const policy = bundle?.editPolicy || resolveSubmissionEditPolicy(submission);
          if (!policy?.canEdit) {
            setError(policy?.message || 'This submission cannot be edited.');
            return;
          }
          setEditPolicy(policy);
          setForm(loadedForm);
          setEligibilities(Array.isArray(loadedForm?.eligibilities) ? loadedForm.eligibilities : []);
          setSubmitterName(submission.submitterName || '');
          setSubmitterEmail(submission.submitterEmail || '');
          setSubmitterMobile(submission.submitterMobile || '');
          setBrandProduct({
            brandsProducts: submission.brandProduct?.brandsProducts || '',
            brandCategory: submission.brandProduct?.brandCategory || '',
            brandWebsite: submission.brandProduct?.brandWebsite || '',
          });
          setExternalCompanyId(normalizeSubmissionRefId(submission.externalCompanyId));
          setExternalCompanyLabel(submission.externalCompanyName || '');
          setExternalCompanyClientId(normalizeSubmissionRefId(submission.externalCompanyClientId));
          setExternalCompanyClientLabel(submission.externalCompanyClientName || '');
          if (bundle?.companyVolumeStats?.qualifyingSubmissionCount != null) {
            setQualifyingSubmissionCount(bundle.companyVolumeStats.qualifyingSubmissionCount);
          }
          const hydrated = hydrateStaffSubmissionState(submission, loadedForm);
          setSubmissionMeta(hydrated.submissionMeta);
          setPricingBaseline(buildPricingBaselineFromSubmission(submission));
          setCommonAnswers(hydrated.commonAnswers);
          setPeople(hydrated.people);
          if (hydrated.submissionCategoryId) setTopLevelCategoryId(hydrated.submissionCategoryId);
          if (submission.industry?._id || submission.industry) {
            setTopLevelIndustryId(String(submission.industry?._id || submission.industry));
          }
          return;
        }

        if (coordinatorPortal && draftId) {
          const res = await getCoordinatorApplicationDraft(token, draftId);
          const bundle = res.data?.data;
          const loadedForm = bundle?.form;
          const submission = bundle?.submission;
          if (!loadedForm || !submission) {
            setError('Draft not found');
            return;
          }
          setForm(loadedForm);
          setEligibilities(Array.isArray(loadedForm?.eligibilities) ? loadedForm.eligibilities : []);
          setSubmitterName(submission.submitterName || '');
          setSubmitterEmail(submission.submitterEmail || '');
          setSubmitterMobile(submission.submitterMobile || '');
          setBrandProduct({
            brandsProducts: submission.brandProduct?.brandsProducts || '',
            brandCategory: submission.brandProduct?.brandCategory || '',
            brandWebsite: submission.brandProduct?.brandWebsite || '',
          });
          setExternalCompanyId(normalizeSubmissionRefId(submission.externalCompanyId));
          setExternalCompanyLabel(submission.externalCompanyName || '');
          setExternalCompanyClientId(normalizeSubmissionRefId(submission.externalCompanyClientId));
          setExternalCompanyClientLabel(submission.externalCompanyClientName || '');
          setSubmissionMeta({
            applicationNumber: '',
            status: 'DRAFT',
            submittedAt: submission.createdAt || null,
            updatedAt: submission.updatedAt || null,
            lastSavedAt: submission.lastSavedAt || null,
            industryName: submission.industryName || '',
            categoryName: submission.categoryName || '',
            formName: submission.formName || loadedForm?.name || '',
            isDraft: true,
          });
          setLastSavedAt(submission.lastSavedAt || submission.updatedAt || null);
          const submissionCategoryId = submission.category?._id || submission.category || '';
          const categoryIdStr = submissionCategoryId ? String(submissionCategoryId) : '';
          setCommonAnswers(answersArrayToMap(submission.answers || [], loadedForm));
          setPeople(peopleFromSubmission(submission.people, loadedForm, categoryIdStr));
          if (categoryIdStr) setTopLevelCategoryId(categoryIdStr);
          if (submission.industry?._id || submission.industry) {
            setTopLevelIndustryId(String(submission.industry?._id || submission.industry));
          }
          return;
        }

        if (coordinatorPortal && submissionId) {
          const res = await getCoordinatorApplicationSubmission(token, submissionId);
          const bundle = res.data?.data;
          const loadedForm = bundle?.form;
          const submission = bundle?.submission;
          if (!loadedForm || !submission) {
            setError('Submission not found');
            return;
          }
          const policy = bundle?.editPolicy || resolveSubmissionEditPolicy(submission);
          if (!readOnly && !policy.canEdit) {
            setError(policy.message || 'This application cannot be edited.');
            return;
          }
          setEditPolicy(policy);
          setForm(loadedForm);
          setEligibilities(Array.isArray(loadedForm?.eligibilities) ? loadedForm.eligibilities : []);
          setSubmitterName(submission.submitterName || '');
          setSubmitterEmail(submission.submitterEmail || '');
          setSubmitterMobile(submission.submitterMobile || '');
          setBrandProduct({
            brandsProducts: submission.brandProduct?.brandsProducts || '',
            brandCategory: submission.brandProduct?.brandCategory || '',
            brandWebsite: submission.brandProduct?.brandWebsite || '',
          });
          setExternalCompanyId(normalizeSubmissionRefId(submission.externalCompanyId));
          setExternalCompanyLabel(submission.externalCompanyName || '');
          setExternalCompanyClientId(normalizeSubmissionRefId(submission.externalCompanyClientId));
          setExternalCompanyClientLabel(submission.externalCompanyClientName || '');
          if (bundle?.companyVolumeStats?.qualifyingSubmissionCount != null) {
            setQualifyingSubmissionCount(bundle.companyVolumeStats.qualifyingSubmissionCount);
          }
          setSubmissionMeta({
            applicationNumber: submission.applicationNumber || '',
            status: submission.status || '',
            submittedAt: submission.createdAt || null,
            updatedAt: submission.updatedAt || null,
            industryName: submission.industryName || '',
            categoryName: submission.categoryName || '',
            formName: submission.formName || loadedForm?.name || '',
            paymentReferenceNumber: submission.paymentReferenceNumber || '',
          });
          setPricingBaseline(buildPricingBaselineFromSubmission(submission));
          const submissionCategoryId = submission.category?._id || submission.category || '';
          const categoryIdStr = submissionCategoryId ? String(submissionCategoryId) : '';
          setCommonAnswers(answersArrayToMap(submission.answers || [], loadedForm));
          setPeople(peopleFromSubmission(submission.people, loadedForm, categoryIdStr));
          if (categoryIdStr) setTopLevelCategoryId(categoryIdStr);
          if (submission.industry?._id || submission.industry) {
            setTopLevelIndustryId(String(submission.industry?._id || submission.industry));
          }
          return;
        }

        const res = isPreviewMode
          ? await previewApplicationFormByToken(token)
          : coordinatorPortal
            ? await getCoordinatorApplicationFormByToken(token)
            : staffCreate
              ? await getStaffApplicationFormByToken(token)
              : await getApplicationFormByToken(token);
        const loaded = res.data?.data;
        setForm(loaded);
        setEligibilities(Array.isArray(loaded?.eligibilities) ? loaded.eligibilities : []);
        if (loaded?.companyVolumeStats?.qualifyingSubmissionCount != null) {
          setQualifyingSubmissionCount(loaded.companyVolumeStats.qualifyingSubmissionCount);
        }
        setLegacyOnboardingRequiresIntent(Boolean(loaded?.legacyOnboarding?.requiresIntentChoice));
        setAllowsClosedRegistrationApply(
          Boolean(
            loaded?.legacyOnboarding?.allowsClosedRegistrationApply
            || loaded?.legacyOnboarding?.companyInvited
          )
        );
        if (!loaded?.legacyOnboarding?.requiresIntentChoice) {
          setLegacySubmissionIntent('');
        }
      } catch (err) {
        const code = err?.response?.data?.code;
        if (code === 'FORM_MARKET_MISMATCH' || code === 'EVENTS_APPLY_DISABLED' || code === 'PORTAL_COMPANY_NOT_FOUND') {
          setError(portalApplyErrorMessage(err, 'This application form is not available for your account.'));
        } else {
          setError(portalApplyErrorMessage(err, 'Form not found'));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token, submissionId, draftId, eventId, coordinatorPortal, staffCreate, staffEdit, staffView, readOnly, isPreviewMode]);

  useEffect(() => {
    setCurrentStepIndex(0);
  }, [form?._id, submissionId]);

  useEffect(() => {
    const t = token || form?.publicToken;
    if (!t) return;
    if (!form?.event?.bulkDiscountsEnabled) return;

    const currentExcludeSubId = submissionId || null;
    if (coordinatorPortal) {
      getCoordinatorApplicationFormVolumeStats(t, { excludeSubmissionId: currentExcludeSubId })
        .then((res) => {
          if (res.data?.data?.qualifyingSubmissionCount != null) {
            setQualifyingSubmissionCount(res.data.data.qualifyingSubmissionCount);
          }
        })
        .catch(() => {});
    } else if (externalCompanyId) {
      getApplicationFormCompanyVolumeStats(t, {
        externalCompanyId,
        excludeSubmissionId: currentExcludeSubId,
      })
        .then((res) => {
          if (res.data?.data?.qualifyingSubmissionCount != null) {
            setQualifyingSubmissionCount(res.data.data.qualifyingSubmissionCount);
          }
        })
        .catch(() => {});
    }
  }, [token, form?.publicToken, form?.event?.bulkDiscountsEnabled, coordinatorPortal, externalCompanyId, submissionId]);

  useEffect(() => {
    taxConfigApi.getPublicEventTaxConfigs()
      .then((res) => setEventTaxConfigs(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setEventTaxConfigs([]));
  }, []);

  useEffect(() => {
    getPublicEventSettings()
      .then((res) => {
        const s = res.data?.data;
        const text = String(s?.registrationPaymentAgreementText || '').trim();
        setRequireAgreement(!!s?.requireRegistrationPaymentAgreement && !!text);
        setAgreementText(text);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const serverUrl = String(form?.event?.logoUrl || '');
    if (serverUrl) {
      setEventLogoSrc(serverUrl);
      return;
    }
    const fileId = String(form?.event?.logoFileId || '');
    if (!fileId) {
      setEventLogoSrc('');
      return;
    }
    fileApi.getPresignedUrl(fileId)
      .then((res) => {
        const url = res.data?.data?.presignedUrl || res.data?.data?.url || res.data?.url || '';
        setEventLogoSrc(url);
      })
      .catch(() => setEventLogoSrc(''));
  }, [form?.event?.logoUrl, form?.event?.logoFileId]);

  const patchPerson = (idx, patch) => {
    setPeople((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const patchPersonAnswer = (idx, key, value) => {
    setPeople((prev) => prev.map((p, i) => (i === idx ? { ...p, answers: { ...(p.answers || {}), [key]: value } } : p)));
  };
  const patchCommonAnswer = (key, value) => {
    setCommonAnswers((prev) => ({ ...(prev || {}), [key]: value }));
  };
  const addPerson = () => {
    if (lockPeopleStructure) return;
    const maxPerSubmission = Number(form?.settings?.people?.maxRows || 100);
    const remaining = form?.capacityInfo ? form.capacityInfo.remaining : maxPerSubmission;
    const effectiveMax = Math.min(maxPerSubmission, remaining);
    setPeople((prev) => (prev.length >= effectiveMax ? prev : [{ ...emptyPerson(), categoryId: topLevelCategoryId || '' }, ...prev]));
  };
  const removePerson = (idx) => {
    if (lockPeopleStructure) return;
    const min = Number(form?.settings?.people?.minRows || 1);
    setPeople((prev) => (prev.length <= min ? prev : prev.filter((_, i) => i !== idx)));
  };

  const orderedSections = useMemo(() => {
    const list = Array.isArray(form?.sections) ? form.sections : [];
    const normalized = [...list]
      .map((s, idx) => ({ ...s, order: Number.isFinite(Number(s?.order)) ? Number(s.order) : idx }))
      .sort((a, b) => a.order - b.order);
    if (normalized.length) return normalized;
    const inferred = [];
    const seen = new Set();
    for (const q of (form?.questions || [])) {
      const key = String(q?.sectionKey || '').trim() || 'general_section';
      if (seen.has(key)) continue;
      seen.add(key);
      inferred.push({ key, title: key.replace(/_/g, ' '), description: '', audience: 'NOMINEE', order: inferred.length });
    }
    return inferred;
  }, [form?.sections, form?.questions]);

  const sectionAudienceByKey = useMemo(() => {
    const m = new Map();
    for (const s of orderedSections) {
      m.set(String(s?.key || ''), String(s?.audience || 'NOMINEE').toUpperCase() === 'COMMON' ? 'COMMON' : 'NOMINEE');
    }
    return m;
  }, [orderedSections]);

  const questionsBySectionKey = useMemo(() => {
    const m = new Map();
    for (const q of (form?.questions || [])) {
      const key = String(q?.sectionKey || orderedSections?.[0]?.key || '');
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(q);
    }
    return m;
  }, [form?.questions, orderedSections]);

  /** Safe answer key per question id — falls back to q.id when fieldKey is shared by multiple questions. */
  const answerKeyByQuestionId = useMemo(() => {
    const allFormQuestions = [
      ...(form?.questions || []),
      ...(form?.categoryQuestionSets || []).flatMap((cqs) => cqs?.questions || []),
    ];
    const fieldKeyCounts = new Map();
    for (const q of allFormQuestions) {
      const fk = String(q?.fieldKey || '');
      if (fk) fieldKeyCounts.set(fk, (fieldKeyCounts.get(fk) || 0) + 1);
    }
    const m = new Map();
    for (const q of allFormQuestions) {
      const fk = String(q?.fieldKey || '');
      const isDuplicate = fk && (fieldKeyCounts.get(fk) || 0) > 1;
      m.set(String(q?.id || ''), isDuplicate ? String(q?.id || fk) : (fk || String(q?.id || '')));
    }
    return m;
  }, [form?.questions, form?.categoryQuestionSets]);

  const answerKey = (q) => answerKeyByQuestionId.get(String(q?.id || '')) || String(q?.fieldKey || q?.id || '');

  const buildAnswersByFieldKey = useCallback((answersObj = {}) => {
    const map = { ...(answersObj || {}) };
    const allFormQuestions = [
      ...(form?.questions || []),
      ...(form?.categoryQuestionSets || []).flatMap((cqs) => cqs?.questions || []),
    ];
    for (const q of allFormQuestions) {
      const ak = answerKey(q);
      if (answersObj?.[ak] === undefined) continue;
      const fk = String(q?.fieldKey || '').trim();
      if (fk) map[fk] = answersObj[ak];
      if (q?.id) map[String(q.id)] = answersObj[ak];
    }
    return map;
  }, [form?.questions, form?.categoryQuestionSets, answerKeyByQuestionId]);

  const questionsByKey = useMemo(() => {
    const map = {};
    const allFormQuestions = [
      ...(form?.questions || []),
      ...(form?.categoryQuestionSets || []).flatMap((cqs) => cqs?.questions || []),
    ];
    for (const q of allFormQuestions) {
      const fk = String(q?.fieldKey || '').trim();
      if (fk) map[fk] = q;
      if (q?.id) map[String(q.id)] = q;
    }
    return map;
  }, [form?.questions, form?.categoryQuestionSets]);

  const commonVisibilityContext = useMemo(
    () => ({ ...buildAnswersByFieldKey(commonAnswers), __questionsByKey: questionsByKey }),
    [buildAnswersByFieldKey, commonAnswers, questionsByKey]
  );

  const buildNomineeVisibilityContext = useCallback((person = {}) => ({
    ...commonVisibilityContext,
    ...buildAnswersByFieldKey(person?.answers || {}),
    fullName: person?.fullName,
    email: person?.email,
    phone: person?.phone,
    designation: person?.designation,
    categoryId: person?.categoryId,
    __questionsByKey: questionsByKey,
  }), [commonVisibilityContext, buildAnswersByFieldKey, questionsByKey]);

  /** Category-specific question set for the currently selected top-level category. */
  const activeCategoryQuestionSet = useMemo(() => {
    const sets = Array.isArray(form?.categoryQuestionSets) ? form.categoryQuestionSets : [];
    const cid = normalizeCategoryIdRef(topLevelCategoryId);
    if (!cid || !sets.length) return null;
    return sets.find((cqs) => normalizeCategoryIdRef(cqs.categoryId) === cid) || null;
  }, [form?.categoryQuestionSets, topLevelCategoryId]);

  const activeCategoryQuestions = useMemo(() => {
    if (!activeCategoryQuestionSet?.questions?.length) return [];
    return [...activeCategoryQuestionSet.questions].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  }, [activeCategoryQuestionSet]);

  const categoryQuestionsSectionKey = useMemo(() => {
    if (!activeCategoryQuestionSet) return '';
    return resolveCategoryQuestionsSectionKey(activeCategoryQuestionSet, orderedSections);
  }, [activeCategoryQuestionSet, orderedSections]);

  const firstNomineeSectionKey = useMemo(() => {
    const hit = orderedSections.find((s) => (sectionAudienceByKey.get(String(s?.key || '')) || 'NOMINEE') === 'NOMINEE');
    return hit ? String(hit.key) : '';
  }, [orderedSections, sectionAudienceByKey]);

  const formSteps = useMemo(() => buildPublicFormSteps(form?.settings), [form?.settings]);
  const paginationEnabled = Boolean(formSteps?.length);
  const currentStep = paginationEnabled ? formSteps[currentStepIndex] : null;
  const isLastStep = paginationEnabled && currentStepIndex >= (formSteps?.length || 0) - 1;
  const showLegacyIntentChoice = coordinatorPortal && !submissionId && !readOnly && !isPreviewMode && legacyOnboardingRequiresIntent;
  const allowStepJump = isPreviewMode || readOnly || (portalLikeEdit && Boolean(submissionId));
  const entryDetailsStep = useMemo(() => findEntryDetailsPage(formSteps || []), [formSteps]);
  const hasEntryDetailsPage = paginationEnabled && Boolean(entryDetailsStep);
  const entryDetailsStepId = entryDetailsStep?.id || 'page_entry_details';
  const showSetupStep = !paginationEnabled || currentStep?.id === SETUP_PAGE_ID;
  const showQuestionSections = !paginationEnabled || (currentStep && currentStep.id !== SETUP_PAGE_ID);

  const sectionsToRender = useMemo(() => {
    if (!paginationEnabled || !currentStep || currentStep.id === SETUP_PAGE_ID) return [];
    return sectionsForPublicFormStep(orderedSections, currentStep.id, form?.settings);
  }, [paginationEnabled, currentStep, orderedSections, form?.settings]);

  const coordinatorSettings = useMemo(() => resolveCoordinatorSettings(form?.settings), [form?.settings]);
  const pdfOutputPacks = useMemo(() => resolvePdfOutputs(form?.settings), [form?.settings]);
  const multiPdfActive = pdfOutputPacks.length > 1;

  const downloadPacksForSubmission = useCallback(async (sid, {
    channel = 'staff',
    dirtyKeys = null,
    applicationNumber = '',
  } = {}) => {
    if (!sid) return;
    try {
      const listFn = channel === 'coordinator'
        ? listCoordinatorSubmissionPdfPacks
        : channel === 'public'
          ? listPublicSubmissionPdfPacks
          : listSubmissionPdfPacks;
      const downloadFn = channel === 'coordinator'
        ? (packKey) => downloadCoordinatorSubmissionPdf(sid, { packKey })
        : channel === 'public'
          ? (packKey) => downloadPublicSubmissionPdf(sid, packKey)
          : (packKey) => downloadSubmissionPdf(sid, packKey);

      const result = await downloadSubmissionPdfPacks({
        listPacks: () => listFn(sid),
        downloadPack: downloadFn,
        applicationNumber: applicationNumber || 'application',
        // Staff often "views" PDFs; multi-pack still downloads a ZIP (browsers block multi-tab opens).
        mode: channel === 'staff' ? 'open' : 'download',
        dirtyKeys,
      });
      if (result.zipped) {
        toast.success(`Downloaded ${result.packCount} PDFs as a ZIP (${result.titles.join(', ')})`);
      }
    } catch (_) { /* non-critical after submit */ }
  }, []);
  const needsClientSelection = useMemo(() => coordinatorNeedsClientSelection(form?.settings), [form?.settings]);
  const requireClientSelection = useMemo(() => coordinatorRequiresClientSelection(form?.settings), [form?.settings]);
  const coordLabels = coordinatorSettings.labels;
  const questionById = useMemo(() => buildApplicationFormQuestionMap(form), [form]);

  const loadPublicCompanyOptions = useCallback(async ({ search, page, limit }) => {
    if (!token) {
      return { options: [], pagination: { page: 1, pages: 1, total: 0, limit } };
    }
    try {
      const params = { page, limit, ...(search ? { search } : {}) };
      const res = isPreviewMode
        ? await getPreviewApplicationFormExternalCompanies(token, params)
        : staffCreate
          ? await getStaffApplicationFormExternalCompanies(token, params)
          : await getPublicApplicationFormExternalCompanies(token, params);
      const data = res.data?.data || {};
      const rows = Array.isArray(data.externalCompanies) ? data.externalCompanies : [];
      return {
        options: rows.map((row) => ({
          value: String(row._id),
          label: row.label || row.companyName || '—',
          description: row.email || '',
          discountPercentage: Number(row.discountPercentage || 0),
          discountNotes: String(row.discountNotes || ''),
        })),
        pagination: data.pagination || { page: 1, pages: 1, total: 0, limit },
      };
    } catch (_) {
      return { options: [], pagination: { page: 1, pages: 1, total: 0, limit } };
    }
  }, [token, isPreviewMode, staffCreate]);

  const refreshClientOptions = useCallback(async (companyId, selectId = null, fallbackClient = null) => {
    const cid = String(companyId || externalCompanyId || '').trim();
    if ((!cid && !coordinatorPortal) || !needsClientSelection) {
      setClientOptions([]);
      return [];
    }
    setClientsLoading(true);
    try {
      let rows = [];
      if (coordinatorPortal) {
        const res = await getCoordinatorMyCompanyClients();
        rows = Array.isArray(res.data?.data) ? res.data.data : [];
      } else if (isPreviewMode && (token || form?.publicToken)) {
        const res = await getPreviewApplicationFormCompanyClients(token || form.publicToken, cid);
        rows = Array.isArray(res.data?.data) ? res.data.data : [];
      } else if (staffCreate && (token || form?.publicToken)) {
        const res = await getStaffApplicationFormCompanyClients(token || form.publicToken, cid);
        rows = Array.isArray(res.data?.data) ? res.data.data : [];
      } else if (token || form?.publicToken) {
        const res = await getPublicApplicationFormCompanyClients(token || form.publicToken, cid);
        rows = Array.isArray(res.data?.data) ? res.data.data : [];
      }
      const opts = rows.map((c) => ({
        value: String(c._id),
        label: c.name || '—',
        description: c.email || '',
      }));
      if (fallbackClient?._id && !opts.some((o) => String(o.value) === String(fallbackClient._id))) {
        opts.unshift({
          value: String(fallbackClient._id),
          label: fallbackClient.name || '—',
          description: fallbackClient.email || '',
        });
      }
      setClientOptions(opts);
      if (selectId) {
        const target = opts.find((o) => String(o.value) === String(selectId));
        if (target) {
          setExternalCompanyClientId(String(target.value));
          setExternalCompanyClientLabel(target.label);
          setFieldErrors((prev) => ({ ...prev, externalCompanyClientId: false }));
        }
      }
      return rows;
    } catch (_) {
      setClientOptions([]);
      return [];
    } finally {
      setClientsLoading(false);
    }
  }, [coordinatorPortal, staffCreate, needsClientSelection, token, form, isPreviewMode, externalCompanyId]);

  useEffect(() => {
    if (!needsClientSelection) {
      setClientOptions([]);
      return;
    }
    if (externalCompanyId) refreshClientOptions(externalCompanyId);
  }, [needsClientSelection, externalCompanyId, coordinatorPortal, refreshClientOptions]);

  // onProgress: optional callback (index, pct) for a specific file slot
  const uploadAnswer = async (question, value, { onProgress, fileSlotOffset = 0, onFileUploaded, onFileError } = {}) => {
    const questionOptions = Array.isArray(question.options) && question.options.length ? question.options : undefined;
    const qId = String(question?.id || question?._id || '');
    const fk = String(question?.fieldKey || question?.id || question?._id || '');
    const base = {
      questionId: qId,
      fieldKey: fk,
      questionLabel: question?.label || '',
      questionType: question?.type || '',
      questionOptions,
    };

    if (question.type === 'TEXT_WITH_FILE') {
      const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : { text: '', files: [] };
      const text = String(raw.text ?? '').trim();
      const rows = Array.isArray(raw.files) ? raw.files : [];
      const uploaded = [];
      let slotIdx = fileSlotOffset;
      for (const file of rows) {
        if (!file) continue;
        if (typeof file === 'object' && file.fileId) {
          uploaded.push(file);
          if (onProgress) onProgress(slotIdx, 100);
          slotIdx++;
          continue;
        }
        if (typeof file === 'string') continue;
        // Pre-upload validation check
        const maxPer = Number(question?.uploadPolicy?.maxFileSizeMB || 10);
        if (file.size && file.size > maxPer * 1024 * 1024) {
          const errMsg = `"${question.label}": File "${file.name}" exceeds maximum allowed size of ${maxPer}MB.`;
          if (onFileError) onFileError(slotIdx, errMsg);
          throw new Error(errMsg);
        }
        const currentSlot = slotIdx;
        slotIdx++;
        try {
          // eslint-disable-next-line no-await-in-loop
          const up = await fileApi.uploadFilePublic(file, {
            category: 'Application Document',
            entityType: 'Application',
            entityId: form?._id,
            title: question.label,
            description: `Uploaded via application form: ${form?.name || form?.publicToken || ''}`.trim(),
          }, {
            onUploadProgress: onProgress
              ? (evt) => { if (evt.total) onProgress(currentSlot, Math.round((evt.loaded / evt.total) * 100)); }
              : undefined,
          });
          const d = up?.data?.data;
          if (d?.fileId && onFileUploaded) {
            onFileUploaded(d.fileId);
          }
          uploaded.push({
            fileId: d?.fileId || null,
            fileName: d?.originalName || file?.name || 'File',
            mimeType: d?.mimeType || file?.type || undefined,
            fileSize: d?.fileSize || file?.size || undefined,
            duration: file?.duration || undefined,
            uploadedAt: d?.uploadedAt || undefined,
          });
          if (onProgress) onProgress(currentSlot, 100);
        } catch (uploadErr) {
          const errMsg = uploadErr?.response?.data?.message || uploadErr?.message || `Failed to upload "${file.name}"`;
          if (onFileError) onFileError(currentSlot, errMsg);
          throw uploadErr;
        }
      }
      return { ...base, value: { text, files: uploaded } };
    }

    if (!isUploadQuestionType(question)) {
      return { ...base, value: coerceAnswerValueForQuestion(question, value) };
    }
    const rows = Array.isArray(value) ? value : (value ? [value] : []);
    const uploaded = [];
    let slotIdx = fileSlotOffset;
    for (const file of rows) {
      if (!file) continue;
      if (typeof file === 'object' && file.fileId) {
        uploaded.push(file);
        if (onProgress) onProgress(slotIdx, 100);
        slotIdx++;
        continue;
      }
      if (typeof file === 'string') continue;
      // Pre-upload validation check
      const maxPer = Number(question?.uploadPolicy?.maxFileSizeMB || (String(question?.type).toUpperCase() === 'VIDEO' ? 500 : 10));
      if (file.size && file.size > maxPer * 1024 * 1024) {
        const errMsg = `"${question.label}": File "${file.name}" exceeds maximum allowed size of ${maxPer}MB.`;
        if (onFileError) onFileError(slotIdx, errMsg);
        throw new Error(errMsg);
      }
      const currentSlot = slotIdx;
      slotIdx++;
      try {
        // eslint-disable-next-line no-await-in-loop
        const up = await fileApi.uploadFilePublic(file, {
          category: 'Application Document',
          entityType: 'Application',
          entityId: form?._id,
          title: question.label,
          description: `Uploaded via application form: ${form?.name || form?.publicToken || ''}`.trim(),
        }, {
          onUploadProgress: onProgress
            ? (evt) => { if (evt.total) onProgress(currentSlot, Math.round((evt.loaded / evt.total) * 100)); }
            : undefined,
        });
        const d = up?.data?.data;
        if (d?.fileId && onFileUploaded) {
          onFileUploaded(d.fileId);
        }
        uploaded.push({
          fileId: d?.fileId || null,
          fileName: d?.originalName || file?.name || 'File',
          mimeType: d?.mimeType || file?.type || undefined,
          fileSize: d?.fileSize || file?.size || undefined,
          duration: file?.duration || undefined,
          uploadedAt: d?.uploadedAt || undefined,
        });
        if (onProgress) onProgress(currentSlot, 100);
      } catch (uploadErr) {
        const errMsg = uploadErr?.response?.data?.message || uploadErr?.message || `Failed to upload "${file.name}"`;
        if (onFileError) onFileError(currentSlot, errMsg);
        throw uploadErr;
      }
    }
    return { ...base, value: uploaded };
  };

  const renderQuestionField = (q, value, onChange, keyPrefix = '', hasError = false, ro = false, answersContext = {}) => (
    <ApplicationQuestionField
      question={q}
      value={value}
      onChange={onChange}
      keyPrefix={keyPrefix}
      hasError={hasError}
      readOnly={ro}
      answersContext={answersContext}
      attachmentDeliveryMode={attachmentDeliveryMode}
    />
  );

  const collectValidationSections = useCallback((newFieldErrors, { topLevelNoCategories = false } = {}) => {
    const sections = new Set();
    if (
      newFieldErrors.submitterName
      || newFieldErrors.submitterEmail
      || newFieldErrors.submitterMobile
      || newFieldErrors.externalCompanyId
      || newFieldErrors.externalCompanyClientId
      || newFieldErrors.brandsProducts
    ) sections.add('General');
    if (newFieldErrors.industryId || newFieldErrors.categoryId || topLevelNoCategories) sections.add('Category');
    if (!people.length) sections.add(peopleLabels.plural);
    for (const pe of newFieldErrors.people) {
      if (pe.fullName || pe.designation || pe.email || pe.phone) sections.add(peopleLabels.plural);
      if (Object.values(pe.answers).some(Boolean)) sections.add('Questions');
    }
    if (Object.values(newFieldErrors.commonAnswers).some(Boolean)) sections.add('Questions');
    return sections;
  }, [people.length, peopleLabels.plural]);

  const buildFieldErrors = useCallback((options = {}) => {
    const { stepId = null, validateAll = false } = options;
    const paginationActive = isPublicFormPaginationEnabled(form?.settings);
    const activeEntryStep = findEntryDetailsPage(formSteps || []);
    const hasEntryDetails = paginationActive && Boolean(activeEntryStep);
    const targetEntryStepId = activeEntryStep?.id || 'page_entry_details';
    const validateCoordinator = validateAll || !stepId || stepId === SETUP_PAGE_ID;
    const validateEntryDetails = validateAll || (hasEntryDetails ? (stepId === targetEntryStepId) : validateCoordinator);
    const validateQuestions = validateAll || (stepId && stepId !== SETUP_PAGE_ID);
    const stepSectionKeys = validateQuestions && stepId && stepId !== SETUP_PAGE_ID
      ? new Set(sectionsForPublicFormStep(orderedSections, stepId, form?.settings).map((s) => String(s?.key || '')))
      : null;
    const validatePersonIdentity = validateAll || (
      hasEntryDetails
        ? (stepId === targetEntryStepId)
        : (paginationActive ? Boolean(stepSectionKeys?.has(firstNomineeSectionKey)) : validateCoordinator)
    );

    const personRequiredFields = Array.isArray(form?.settings?.people?.requiredFields)
      ? form.settings.people.requiredFields
      : [];
    const topLevelIndustryError = !categoryOnlyIntake && uniqueIndustries.length > 0 && !topLevelIndustryId.trim();
    const topLevelNoCategories = categoryOnlyIntake
      ? activeRootCategories.length === 0
      : (!!topLevelIndustryId && activeRootCategories.length === 0);
    const topLevelCategoryError = leafEntryOptions.length > 0 && !topLevelNoCategories && !topLevelCategoryId.trim();

    const questionOnStep = (q) => {
      if (!validateQuestions) return false;
      if (validateAll || !stepSectionKeys) return true;
      const sk = String(q?.sectionKey || orderedSections?.[0]?.key || '');
      return stepSectionKeys.has(sk);
    };

    const includeCategoryQuestions = validateAll
      || (stepSectionKeys && categoryQuestionsSectionKey && stepSectionKeys.has(categoryQuestionsSectionKey));
    // Judging-day staff corrections may change category/names without re-answering every
    // question; backend already skips category required checks in staff_correction.
    const skipAnswerRequired =
      Boolean(staffEdit && editPolicy?.editMode === 'staff_correction');

    return {
      submitterName: validateCoordinator ? !submitterName.trim() : false,
      submitterEmail: validateCoordinator ? !submitterEmail.trim() : false,
      submitterMobile: validateCoordinator ? !submitterMobile.trim() : false,
      externalCompanyId: validateEntryDetails && !portalLikeEdit ? !externalCompanyId.trim() : false,
      externalCompanyClientId: validateEntryDetails && requireClientSelection ? !externalCompanyClientId.trim() : false,
      industryId: validateEntryDetails ? topLevelIndustryError : false,
      categoryId: validateEntryDetails ? topLevelCategoryError : false,
      brandsProducts: validateEntryDetails && Boolean(form?.settings?.requireBrandProduct) ? !String(brandProduct.brandsProducts || '').trim() : false,
      people: people.map((p) => {
        const answersErrors = {};
        if (!skipAnswerRequired) {
          for (const q of (form?.questions || [])) {
            const sk = String(q?.sectionKey || orderedSections?.[0]?.key || '');
            const aud = sectionAudienceByKey.get(sk) || 'NOMINEE';
            if (aud === 'COMMON' || skipsAnswerCollection(q)) continue;
            if (!questionOnStep(q)) continue;
            if (!isQuestionVisible(q, buildNomineeVisibilityContext(p))) continue;
            const v = p.answers?.[answerKey(q)];
            const required = Boolean(q?.validationRules?.required ?? q?.required);
            const pctOutOfRange = String(q?.type || '').toUpperCase() === 'PERCENTAGE' && Boolean(percentageRangeError(q, v));
            if ((required && isQuestionValueEmpty(q, v)) || pctOutOfRange) answersErrors[answerKey(q)] = true;
          }
          if (includeCategoryQuestions) {
            for (const q of activeCategoryQuestions) {
              if (skipsAnswerCollection(q)) continue;
              if (!isQuestionVisible(q, buildNomineeVisibilityContext(p))) continue;
              const ak = answerKey(q);
              const v = p.answers?.[ak];
              const required = Boolean(q?.validationRules?.required ?? q?.required);
              const pctOutOfRange = String(q?.type || '').toUpperCase() === 'PERCENTAGE' && Boolean(percentageRangeError(q, v));
              if ((required && isQuestionValueEmpty(q, v)) || pctOutOfRange) answersErrors[ak] = true;
            }
          }
        }
        return {
          fullName: validatePersonIdentity ? !String(p.fullName || '').trim() : false,
          designation: validatePersonIdentity && !hasEntryDetails && personRequiredFields.includes('designation') && !String(p.designation || '').trim(),
          email: validatePersonIdentity && !hasEntryDetails && !String(p.email || '').trim(),
          phone: validatePersonIdentity && !hasEntryDetails && !String(p.phone || '').trim(),
          answers: answersErrors,
        };
      }),
      commonAnswers: (() => {
        const errs = {};
        if (skipAnswerRequired) return errs;
        if (!validateQuestions && !validateAll) return errs;
        for (const q of (form?.questions || [])) {
          const sk = String(q?.sectionKey || orderedSections?.[0]?.key || '');
          const aud = sectionAudienceByKey.get(sk) || 'NOMINEE';
          if (aud !== 'COMMON' || skipsAnswerCollection(q)) continue;
          if (!questionOnStep(q)) continue;
          if (!isQuestionVisible(q, commonVisibilityContext)) continue;
          const v = commonAnswers?.[answerKey(q)];
          const required = Boolean(q?.validationRules?.required ?? q?.required);
          const pctOutOfRange = String(q?.type || '').toUpperCase() === 'PERCENTAGE' && Boolean(percentageRangeError(q, v));
          if ((required && isQuestionValueEmpty(q, v)) || pctOutOfRange) errs[answerKey(q)] = true;
        }
        return errs;
      })(),
      _topLevelNoCategories: topLevelNoCategories,
    };
  }, [
    activeCategoryQuestions,
    activeRootCategories,
    allEligibilityCategories,
    answerKey,
    brandProduct.brandsProducts,
    buildNomineeVisibilityContext,
    categoryOnlyIntake,
    commonAnswers,
    commonVisibilityContext,
    editPolicy?.editMode,
    externalCompanyClientId,
    externalCompanyId,
    categoryQuestionsSectionKey,
    firstNomineeSectionKey,
    form?.questions,
    form?.settings,
    leafEntryOptions.length,
    orderedSections,
    people,
    portalLikeEdit,
    requireClientSelection,
    sectionAudienceByKey,
    staffEdit,
    submitterEmail,
    submitterMobile,
    submitterName,
    topLevelCategoryId,
    topLevelIndustryId,
    uniqueIndustries.length,
  ]);

  const goToStep = useCallback((nextIndex) => {
    setIsStepChanging(true);
    setCurrentStepIndex(nextIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setIsStepChanging(false), 450);
  }, []);

  const applyFieldErrors = useCallback((rawErrors) => {
    const { _topLevelNoCategories, ...errs } = rawErrors;
    setFieldErrors(errs);
    return { errs, topLevelNoCategories: _topLevelNoCategories };
  }, []);

  const clearFieldErrors = useCallback(() => {
    setFieldErrors({
      submitterName: false,
      submitterEmail: false,
      submitterMobile: false,
      externalCompanyId: false,
      externalCompanyClientId: false,
      industryId: false,
      categoryId: false,
      brandsProducts: false,
      people: people.map(() => ({
        fullName: false,
        designation: false,
        email: false,
        phone: false,
        answers: {},
      })),
      commonAnswers: {},
    });
  }, [people]);

  const handleStepClick = useCallback((nextIndex) => {
    if (isPreviewMode || readOnly) {
      clearFieldErrors();
      setValidationSections([]);
    }
    goToStep(nextIndex);
  }, [clearFieldErrors, goToStep, isPreviewMode, readOnly]);

  const handleNextStep = () => {
    if (isStepChanging) return;
    if (!paginationEnabled || readOnly || isPreviewMode) {
      clearFieldErrors();
      setValidationSections([]);
      goToStep(Math.min(currentStepIndex + 1, (formSteps?.length || 1) - 1));
      return;
    }
    const newFieldErrors = buildFieldErrors({ stepId: currentStep?.id, validateAll: false });
    const { errs, topLevelNoCategories } = applyFieldErrors(newFieldErrors);
    const sections = collectValidationSections(errs, { topLevelNoCategories });
    if (sections.size) {
      setValidationSections([...sections]);
      toast.error('Please complete the required fields on this page before continuing.');
      return;
    }
    setValidationSections([]);
    goToStep(Math.min(currentStepIndex + 1, (formSteps?.length || 1) - 1));
  };

  const handlePreviousStep = () => {
    if (currentStepIndex <= 0) return;
    setValidationSections([]);
    goToStep(currentStepIndex - 1);
  };

  const buildSubmissionPayload = useCallback(async ({ onFileProgress, onFileUploaded, onFileError } = {}) => {
    const normalizedCommonAnswers = [];
    let slotOffset = 0;
    for (const q of (form?.questions || [])) {
      const sectionKey = String(q?.sectionKey || orderedSections?.[0]?.key || '');
      const audience = sectionAudienceByKey.get(sectionKey) || 'NOMINEE';
      if (audience !== 'COMMON') continue;
      if (skipsAnswerCollection(q)) continue;
      // eslint-disable-next-line no-await-in-loop
      const ans = await uploadAnswer(q, commonAnswers?.[answerKey(q)], {
        onProgress: onFileProgress,
        fileSlotOffset: slotOffset,
        onFileUploaded,
        onFileError,
      });
      // Advance slotOffset by the number of new files in this answer
      const newFiles = countNewFilesInAnswer(q, commonAnswers?.[answerKey(q)]);
      slotOffset += newFiles;
      normalizedCommonAnswers.push(ans);
    }
    const normalizedPeople = [];
    for (const p of people) {
      const answers = [];
      for (const q of (form?.questions || [])) {
        const sectionKey = String(q?.sectionKey || orderedSections?.[0]?.key || '');
        const audience = sectionAudienceByKey.get(sectionKey) || 'NOMINEE';
        if (audience === 'COMMON') continue;
        if (skipsAnswerCollection(q)) continue;
        // eslint-disable-next-line no-await-in-loop
        const ans = await uploadAnswer(q, p.answers?.[answerKey(q)], {
          onProgress: onFileProgress,
          fileSlotOffset: slotOffset,
          onFileUploaded,
          onFileError,
        });
        const newFiles = countNewFilesInAnswer(q, p.answers?.[answerKey(q)]);
        slotOffset += newFiles;
        answers.push(ans);
      }
      const personCategoryId = normalizeCategoryIdRef(p.categoryId || topLevelCategoryId);
      const personCatSet = (form?.categoryQuestionSets || []).find(
        (cqs) => normalizeCategoryIdRef(cqs.categoryId) === personCategoryId
      );
      for (const q of (personCatSet?.questions || [])) {
        if (skipsAnswerCollection(q)) continue;
        // eslint-disable-next-line no-await-in-loop
        const ans = await uploadAnswer(q, p.answers?.[answerKey(q)], {
          onProgress: onFileProgress,
          fileSlotOffset: slotOffset,
          onFileUploaded,
          onFileError,
        });
        const newFiles = countNewFilesInAnswer(q, p.answers?.[answerKey(q)]);
        slotOffset += newFiles;
        answers.push(ans);
      }
      normalizedPeople.push({
        fullName: String(p.fullName || '').trim(),
        designation: String(p.designation || '').trim(),
        email: String(p.email || submitterEmail || '').trim(),
        phone: String(p.phone || submitterMobile || '').trim(),
        categoryId: personCategoryId,
        answers,
      });
    }
    return {
      submitterName,
      submitterEmail,
      submitterMobile,
      ...(topLevelIndustryId ? { industryId: topLevelIndustryId } : {}),
      ...(topLevelCategoryId ? { categoryId: topLevelCategoryId } : {}),
      answers: normalizedCommonAnswers,
      people: normalizedPeople,
      brandProduct: {
        brandsProducts: String(brandProduct.brandsProducts || '').trim(),
        brandCategory: String(brandProduct.brandCategory || '').trim(),
        brandWebsite: String(brandProduct.brandWebsite || '').trim(),
      },
      ...(externalCompanyId ? { externalCompanyId } : {}),
      ...(externalCompanyClientId ? { externalCompanyClientId } : {}),
      ...(legacySubmissionIntent ? { legacySubmissionIntent } : {}),
      ...(appliedPromo?.code ? { promoCode: appliedPromo.code } : {}),
    };
  }, [
    answerKey,
    appliedPromo?.code,
    brandProduct,
    commonAnswers,
    externalCompanyClientId,
    externalCompanyId,
    legacySubmissionIntent,
    form?.categoryQuestionSets,
    form?.questions,
    orderedSections,
    people,
    sectionAudienceByKey,
    submitterEmail,
    submitterMobile,
    submitterName,
    topLevelCategoryId,
    topLevelIndustryId,
  ]);

  const handleSaveDraft = async () => {
    if (!coordinatorPortal || readOnly || isPreviewMode || submitted) return;
    const newlyUploadedDraftFileIds = [];
    try {
      setDraftSaving(true);
      const payload = await buildSubmissionPayload({
        onFileUploaded: (fid) => { if (fid) newlyUploadedDraftFileIds.push(fid); }
      });
      if (draftId) {
        const res = await updateCoordinatorApplicationDraft(token, draftId, payload);
        const savedAt = res.data?.data?.lastSavedAt;
        setLastSavedAt(savedAt || new Date().toISOString());
      } else {
        const res = await saveCoordinatorApplicationDraft(token, payload);
        const newId = res.data?.data?.draftId;
        const savedAt = res.data?.data?.lastSavedAt;
        setLastSavedAt(savedAt || new Date().toISOString());
        if (newId) {
          navigate(`/events/external-portal/apply/${token}/draft/${newId}`, { replace: true, state: location.state });
        }
      }
      // Keep component state in sync with uploaded file metadata only after successful draft save
      setCommonAnswers(answersArrayToMap(payload.answers || [], form));
      setPeople(peopleFromSubmission(payload.people || [], form, topLevelCategoryId));
      toast.success('Draft saved — you can return later to finish and submit.');
    } catch (err) {
      if (newlyUploadedDraftFileIds.length > 0) {
        try {
          await fileApi.cleanupPublicUploads(newlyUploadedDraftFileIds);
        } catch (cleanupErr) {
          console.warn('Failed to clean up draft files on error:', cleanupErr);
        }
      }
      const msg = portalApplyErrorMessage(err, 'Failed to save draft');
      if (err?.response?.data?.code === 'FORM_MARKET_MISMATCH') {
        setError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setDraftSaving(false);
    }
  };

  const executeSubmission = async (confirmProformaCancel = false, confirmFinanceAck = false) => {
    // ── Collect all pending (new) files from answers so we can show a progress overlay ──
    const pendingFiles = [];
    const collectPendingFilesFromAnswers = (questionsArr, answersMap) => {
      for (const q of (questionsArr || [])) {
        if (skipsAnswerCollection(q)) continue;
        const val = answersMap?.[answerKey(q)];
        if (!val) continue;
        if (isUploadQuestionType(q)) {
          const rows = Array.isArray(val) ? val : [val];
          for (const f of rows) {
            if (f && typeof f === 'object' && !f.fileId && typeof f !== 'string') {
              pendingFiles.push({ name: f.name || 'File', progress: 0, done: false, error: false });
            }
          }
        } else if (q.type === 'TEXT_WITH_FILE') {
          const rows = Array.isArray(val?.files) ? val.files : [];
          for (const f of rows) {
            if (f && typeof f === 'object' && !f.fileId && typeof f !== 'string') {
              pendingFiles.push({ name: f.name || 'File', progress: 0, done: false, error: false });
            }
          }
        }
      }
    };
    collectPendingFilesFromAnswers(form?.questions, commonAnswers);
    for (const p of people) {
      collectPendingFilesFromAnswers(form?.questions, p.answers);
      const personCategoryId = normalizeCategoryIdRef(p.categoryId || topLevelCategoryId);
      const personCatSet = (form?.categoryQuestionSets || []).find(
        (cqs) => normalizeCategoryIdRef(cqs.categoryId) === personCategoryId
      );
      collectPendingFilesFromAnswers(personCatSet?.questions, p.answers);
    }

    const totalFiles = pendingFiles.length;
    if (totalFiles > 0) {
      setUploadOverlay({ visible: true, files: pendingFiles, overallPct: 0, phase: 'uploading', errorMsg: '' });
    }

    const onFileProgress = totalFiles > 0
      ? (slotIndex, pct) => {
          setUploadOverlay((prev) => {
            const updated = [...prev.files];
            if (updated[slotIndex]) {
              updated[slotIndex] = { ...updated[slotIndex], progress: pct, done: pct >= 100, error: false };
            }
            const completedPct = updated.reduce((sum, f) => sum + (f.progress || 0), 0) / totalFiles;
            return { ...prev, files: updated, overallPct: completedPct, phase: 'uploading' };
          });
        }
      : undefined;

    const newlyUploadedFileIds = [];
    const onFileUploaded = (fileId) => {
      if (fileId) newlyUploadedFileIds.push(fileId);
    };

    const onFileError = (slotIndex, errMsg) => {
      setUploadOverlay((prev) => {
        const updated = [...prev.files];
        if (updated[slotIndex]) {
          updated[slotIndex] = { ...updated[slotIndex], error: true };
        }
        return {
          ...prev,
          files: updated,
          phase: 'error',
          errorMsg: errMsg || 'File upload failed',
        };
      });
    };

    try {
      setSubmitting(true);
      const payload = await buildSubmissionPayload({
        onFileProgress,
        onFileUploaded,
        onFileError,
      });

      // Switch overlay phase to submitting now that all uploads are done
      if (totalFiles > 0) {
        setUploadOverlay((prev) => ({ ...prev, overallPct: 100, phase: 'submitting' }));
      }
      if (confirmProformaCancel) {
        payload.confirmProformaCancel = true;
      }
      if (confirmFinanceAck) {
        payload.confirmFinanceAck = true;
      }

      // Helper to finalize success state
      const handleSubmissionSuccess = () => {
        setCommonAnswers(answersArrayToMap(payload.answers || [], form));
        setPeople(peopleFromSubmission(payload.people || [], form, topLevelCategoryId));
        setSubmitted(true);
        setUploadOverlay({ visible: false, files: [], overallPct: 0, phase: 'uploading', errorMsg: '' });
      };

      if (staffEdit && eventId && submissionId) {
        const updateRes = await updateEventApplicationFormSubmission(eventId, submissionId, payload);
        const updatedSub = updateRes.data?.data;
        const dirtyKeys = updateRes.data?.dirtyPdfPackKeys;
        const extraMsg = updateRes.data?.message;
        const sync = updateRes.data?.judgingSync;
        toast.success(extraMsg || 'Submission updated');
        if (sync?.seatsCleared > 0) {
          toast.success(`${sync.seatsCleared} schedule seat(s) cleared — reseat this applicant on the correct panel.`);
        }
        handleSubmissionSuccess();
        if (updatedSub?._id) {
          setLastSubmissionId(updatedSub._id);
          try {
            await downloadPacksForSubmission(updatedSub._id, {
              channel: 'staff',
              dirtyKeys: multiPdfActive ? dirtyKeys : null,
              applicationNumber: updatedSub.applicationNumber,
            });
          } catch (_) { /* non-critical */ }
        }
        return;
      }

      if (coordinatorPortal && submissionId) {
        const updateRes = await updateCoordinatorApplicationSubmission(token, submissionId, payload);
        const updatedSub = updateRes.data?.data;
        const dirtyKeys = updateRes.data?.dirtyPdfPackKeys;
        toast.success('Submission updated');
        handleSubmissionSuccess();
        if (updatedSub?._id) {
          setLastSubmissionId(updatedSub._id);
          try {
            await downloadPacksForSubmission(updatedSub._id, {
              channel: 'coordinator',
              dirtyKeys: multiPdfActive ? dirtyKeys : null,
              applicationNumber: updatedSub.applicationNumber,
            });
          } catch (_) { /* non-critical — save already succeeded */ }
        }
        return;
      }

      if (coordinatorPortal) {
        const res = await coordinatorSubmitApplicationForm(token, {
          ...payload,
          ...(draftId ? { draftId } : {}),
        });
        const num = res.data?.data?.applicationNumber;
        const sid = res.data?.data?.submissionId;
        if (num) setLastApplicationNumber(num);
        toast.success(form?.settings?.successMessage || 'Thank you for your submission!');
        handleSubmissionSuccess();
        if (sid) {
          setLastSubmissionId(sid);
          try {
            await downloadPacksForSubmission(sid, {
              channel: 'coordinator',
              applicationNumber: num,
            });
          } catch (_) { /* non-critical — confirmation email also carries the PDF */ }
        }
        return;
      }

      if (staffCreate) {
        const res = await staffSubmitApplicationFormOnBehalf(token, payload);
        const num = res.data?.data?.applicationNumber;
        const sid = res.data?.data?.submissionId;
        if (num) setLastApplicationNumber(num);
        toast.success('Application submitted on behalf of the external company.');
        handleSubmissionSuccess();
        if (sid) {
          setLastSubmissionId(sid);
          try {
            await downloadPacksForSubmission(sid, {
              channel: 'staff',
              applicationNumber: num,
            });
          } catch (_) { /* non-critical — email also carries the PDF */ }
        }
        return;
      }

      const res = await submitPublicApplicationForm(token, payload);
      const num = res.data?.data?.applicationNumber;
      const sid = res.data?.data?.submissionId;
      if (num) setLastApplicationNumber(num);
      toast.success(form?.settings?.successMessage || 'Thank you for your submission!');
      handleSubmissionSuccess();
      if (sid) {
        setLastSubmissionId(sid);
        try {
          await downloadPacksForSubmission(sid, {
            channel: 'public',
            applicationNumber: num,
          });
        } catch (_) { /* non-critical */ }
      }
    } catch (err) {
      console.error('Submission or upload failure:', err);

      // Clean up any files that were uploaded during this failed attempt
      // so no orphaned files or partial application data remain in storage/DB!
      if (newlyUploadedFileIds.length > 0) {
        try {
          await fileApi.cleanupPublicUploads(newlyUploadedFileIds);
        } catch (cleanupErr) {
          console.warn('Failed to clean up files after submission failure:', cleanupErr);
        }
      }

      const code = err.response?.data?.code;
      if (code === 'FORM_MARKET_MISMATCH' || code === 'EVENTS_APPLY_DISABLED') {
        setError(portalApplyErrorMessage(err, 'This application form is not available for your account.'));
        return;
      }
      if (code === 'FORM_CAPACITY_FULL' || code === 'FORM_CAPACITY_EXCEEDED') {
        try {
          const res = coordinatorPortal
            ? await getCoordinatorApplicationFormByToken(token)
            : staffCreate
              ? await getStaffApplicationFormByToken(token)
              : await getApplicationFormByToken(token);
          setForm(res.data?.data);
        } catch (_) { /* ignore */ }
        toast.error(err.response?.data?.message || 'No more application slots available');
        return;
      }
      if (code === 'PROFORMA_CANCEL_CONFIRMATION_REQUIRED') {
        setProformaCancelPromptOpen(true);
        return;
      }
      if (code === 'STAFF_CORRECTION_FINANCE_ACK_REQUIRED') {
        setFinanceAckPromptOpen(true);
        return;
      }
      const msg = String(portalApplyErrorMessage(err, '') || err.response?.data?.message || '').toLowerCase();
      const tabs = new Set();
      if (msg.includes('category') || msg.includes('industry') || msg.includes('subcategory')) tabs.add('Category');
      if (msg.includes('people') || msg.includes('person')) tabs.add(peopleLabels.plural);
      if (msg.includes('question') || msg.includes('required')) tabs.add('Questions');
      if (!tabs.size) tabs.add('General');
      setValidationSections([...tabs]);

      const friendlyErrMsg = portalApplyErrorMessage(err, 'Failed to submit form');
      setUploadOverlay((prev) => {
        if (!prev.visible) return prev;
        return {
          ...prev,
          phase: 'error',
          errorMsg: friendlyErrMsg || err.message || 'File upload or submission failed. Partial data was cleaned up.',
        };
      });
      toast.error(friendlyErrMsg);
    } finally {
      setSubmitting(false);
      setUploadOverlay((prev) => {
        if (prev.phase === 'error') return prev;
        return { visible: false, files: [], overallPct: 0, phase: 'uploading', errorMsg: '' };
      });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isStepChanging) return;
    if (isPreviewMode) {
      return toast.error('Preview only — submissions are disabled.');
    }
    if (paginationEnabled && !isLastStep) {
      handleNextStep();
      return;
    }
    if (paginationEnabled && currentStepIndex < (formSteps?.length || 0) - 1) {
      handleNextStep();
      return;
    }

    const newFieldErrors = buildFieldErrors({ validateAll: true });
    const { errs, topLevelNoCategories } = applyFieldErrors(newFieldErrors);
    const sections = collectValidationSections(errs, { topLevelNoCategories });

    if (sections.size) {
      setValidationSections([...sections]);
      return toast.error('Please check highlighted sections before submitting.');
    }
    setValidationSections([]);

    if (form?.settings?.requireBrandProduct && !String(brandProduct.brandsProducts || '').trim()) {
      const brandProductNameLabel = form?.settings?.brandProductLabel
        || form?.settings?.people?.labels?.brandProduct
        || (hasEntryDetailsPage ? 'Brand / Name' : 'Brand / product');
      return toast.error(`${brandProductNameLabel} is required.`);
    }

    if (requireAgreement && !agreementChecked) {
      setAgreementError(true);
      return toast.error('Please accept the agreement before submitting.');
    }
    setAgreementError(false);

    if (coordinatorPortal && !submissionId && legacyOnboardingRequiresIntent && !legacySubmissionIntent) {
      return toast.error('Please choose whether this is a resubmission of a previous offline application or a new application.');
    }

    const isEditFlow = Boolean((staffEdit || coordinatorPortal) && submissionId && pricingBaseline);
    const pricingWillChange = isEditFlow
      && editPolicy?.pricingChangeRequiresProformaCancel
      && submissionPricingWillChange(pricingBaseline, form, {
        industryId: topLevelIndustryId,
        categoryId: topLevelCategoryId,
        people: people.map((p) => ({
          ...p,
          categoryId: p.categoryId || topLevelCategoryId,
        })),
      });

    if (pricingWillChange) {
      setProformaCancelPromptOpen(true);
      return;
    }

    const staffFinanceAckNeeded = isEditFlow
      && staffEdit
      && editPolicy?.pricingChangeRequiresFinanceAck
      && submissionPricingWillChange(pricingBaseline, form, {
        industryId: topLevelIndustryId,
        categoryId: topLevelCategoryId,
        people: people.map((p) => ({
          ...p,
          categoryId: p.categoryId || topLevelCategoryId,
        })),
      });

    if (staffFinanceAckNeeded) {
      setFinanceAckPromptOpen(true);
      return;
    }

    await executeSubmission(false);
  };

  if (loading) return <EventLoadingState variant="page" message="Loading form..." className="min-h-screen print:min-h-0 print:h-auto" />;
  if (error) {
    return (
      <div className="min-h-screen print:min-h-0 print:h-auto flex flex-col items-center justify-center px-4 text-center space-y-3">
        <p className="text-red-600 max-w-xl">{error}</p>
        {coordinatorPortal && (
          <button type="button" onClick={handleBackToPortal} className="text-sm text-indigo-600 hover:underline">← Back to external portal</button>
        )}
        {staffEdit && eventId && (
          <button type="button" onClick={handleBackToSubmissions} className="text-sm text-indigo-600 hover:underline">← Back to submissions</button>
        )}
        {(staffView || staffCreate) && eventId && (
          <button type="button" onClick={handleBackToSubmissions} className="text-sm text-indigo-600 hover:underline">← Back to submissions</button>
        )}
      </div>
    );
  }
  const REGISTRATION_OPEN_STATUSES = ['REGISTRATION_OPENED', 'REGISTRATION_DEADLINE_1', 'REGISTRATION_DEADLINE_2', 'REGISTRATION_DEADLINE_3'];
  // Form invitations (incl. legacy invite) may apply after registration closes — do not hard-block the portal UI.
  if (!isPreviewMode && !staffSubmissionMode && !staffCreate && !portalDraftMode && !submissionId && !allowsClosedRegistrationApply && form?.event && !REGISTRATION_OPEN_STATUSES.includes(form.event.status)) {
    const regStart = form?.event?.registrationStart ? new Date(form.event.registrationStart) : null;
    const notYetOpen = regStart && new Date() < regStart;
    const formattedStart = regStart
      ? regStart.toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
      : null;
    return (
      <div className="min-h-screen print:min-h-0 print:h-auto flex flex-col bg-gray-50">
        <PublicSlimHeader event={form?.event} />
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          {notYetOpen ? (
            <>
              <h2 className="text-xl font-bold text-gray-900">Registration Not Open Yet</h2>
              <p className="text-gray-600 max-w-md">
                Applications for this event will open on <span className="font-semibold text-gray-800">{formattedStart}</span>. Please come back then to submit your application.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-gray-900">Registration Closed</h2>
              <p className="text-gray-600 max-w-md">
                The registration period for this event has ended and no further applications are being accepted.
              </p>
            </>
          )}
          {coordinatorPortal && (
            <button type="button" onClick={handleBackToPortal} className="text-sm text-indigo-600 hover:underline">← Back to external portal</button>
          )}
        </div>
      </div>
    );
  }

  if (!isPreviewMode && !staffSubmissionMode && !portalDraftMode && form?.capacityInfo?.isFull) {
    return (
      <div className="min-h-screen print:min-h-0 print:h-auto flex flex-col bg-gray-50">
        <PublicSlimHeader event={form?.event} />
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Applications Closed</h2>
          <p className="text-gray-600 max-w-md">
            This event has reached its maximum number of applications ({form.capacityInfo.total}). No further submissions are being accepted.
          </p>
          {coordinatorPortal && (
            <button type="button" onClick={handleBackToPortal} className="text-sm text-indigo-600 hover:underline">← Back to external portal</button>
          )}
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen print:min-h-0 print:h-auto flex flex-col items-center justify-center px-4 text-center space-y-4">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600 mb-1">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-green-700 font-semibold text-lg">
          {(coordinatorPortal || staffEdit) && submissionId
            ? 'Your changes were saved.'
            : (form?.settings?.successMessage || 'Submission complete.')}
        </p>
        {lastApplicationNumber && (
          <p className="text-sm text-gray-700">Application number: <span className="font-mono font-bold text-gray-900">{lastApplicationNumber}</span></p>
        )}
        {lastSubmissionId && (
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                const channel = coordinatorPortal ? 'coordinator' : (staffEdit || staffCreate ? 'staff' : 'public');
                downloadPacksForSubmission(lastSubmissionId, {
                  channel,
                  applicationNumber: lastApplicationNumber,
                });
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Application PDF
            </button>
            {coordinatorPortal && (
              <button
                type="button"
                disabled={emailingPdf}
                onClick={async () => {
                  setEmailingPdf(true);
                  try {
                    const res = await emailCoordinatorSubmissionPdf(lastSubmissionId);
                    toast.success(res?.data?.message || 'Latest application PDF sent to your email');
                  } catch (err) {
                    toast.error(err?.response?.data?.message || 'Failed to email application PDF');
                  } finally {
                    setEmailingPdf(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 hover:text-indigo-600 hover:border-indigo-300 text-gray-700 text-sm font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 cursor-pointer"
              >
                {emailingPdf ? (
                  <>
                    <span className="inline-block animate-spin mr-1">⌛</span>
                    Sending PDF…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Me Updated PDF
                  </>
                )}
              </button>
            )}
          </div>
        )}
        {!coordinatorPortal && !staffCreate && (
          <p className="text-sm text-gray-500 max-w-lg">
            This link cannot be used to change your answers after submission. If you work for a coordinator or agency with portal access, sign in and open the external portal to update entries there (when the organiser allows it).
          </p>
        )}
        {coordinatorPortal && (
          <button type="button" onClick={handleBackToPortal} className="text-sm text-indigo-600 hover:underline">Back to external portal</button>
        )}
        {(staffEdit || staffCreate) && eventId && (
          <button type="button" onClick={handleBackToSubmissions} className="text-sm text-indigo-600 hover:underline">Back to submissions</button>
        )}
      </div>
    );
  }

  const renderAgencyAndClientFields = () => (
    <>
      {portalLikeEdit && !allowCompanyChange ? (
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-900 mb-1">{coordLabels.companyLabel}</label>
          <input
            type="text"
            value={externalCompanyLabel || externalCompanyId || '—'}
            readOnly
            disabled
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm bg-gray-50 cursor-not-allowed"
          />
        </div>
      ) : (
        <div className="md:col-span-2">
          <AsyncSearchableSelect
            label={coordLabels.companyLabel}
            value={externalCompanyId}
            displayLabel={externalCompanyLabel}
            onChange={(nextValue, option) => {
              if (readOnly) return;
              const nextId = String(nextValue || '');
              setExternalCompanyId(nextId);
              setExternalCompanyLabel(nextId && option?.label ? String(option.label) : '');
              setExternalCompanyDiscountPercentage(Number(option?.discountPercentage || 0));
              setExternalCompanyDiscountNotes(String(option?.discountNotes || ''));
              setExternalCompanyClientId('');
              setExternalCompanyClientLabel('');
              if (fieldErrors.externalCompanyId) {
                setFieldErrors((prev) => ({ ...prev, externalCompanyId: false }));
              }
            }}
            loadOptions={loadPublicCompanyOptions}
            placeholder={`Select ${coordLabels.companyLabel.toLowerCase()}`}
            searchPlaceholder="Search company / agency…"
            required={!readOnly}
            disabled={readOnly}
            error={fieldErrors.externalCompanyId ? `${coordLabels.companyLabel} is required` : ''}
            allowClear={!readOnly && !allowCompanyChange}
            size="md"
            limit={30}
          />
          {allowCompanyChange && (
            <p className="mt-1 text-xs text-amber-700">
              Changing company updates judging labels and finance customer names on linked invoices/receipts.
            </p>
          )}
        </div>
      )}
      {needsClientSelection && (
        <div className="md:col-span-2">
          {readOnly ? (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">{coordLabels.clientLabel}</label>
              <input
                type="text"
                value={externalCompanyClientLabel || externalCompanyClientId || '—'}
                readOnly
                disabled
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm bg-gray-50 cursor-not-allowed"
              />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-900">
                  {coordLabels.clientLabel} {requireClientSelection && <span className="text-red-500">*</span>}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (!externalCompanyId && !portalLikeEdit) {
                      toast.error(`Please select ${coordLabels.companyLabel.toLowerCase()} first`);
                      return;
                    }
                    setShowAddClientModal(true);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add New Client</span>
                </button>
              </div>
              <SearchableSelect
                value={externalCompanyClientId}
                onChange={(e) => {
                  const nextId = String(e.target.value || '');
                  setExternalCompanyClientId(nextId);
                  const opt = clientOptions.find((o) => String(o.value) === nextId);
                  setExternalCompanyClientLabel(opt?.label || '');
                  if (fieldErrors.externalCompanyClientId) {
                    setFieldErrors((prev) => ({ ...prev, externalCompanyClientId: false }));
                  }
                }}
                options={clientOptions}
                placeholder={
                  !externalCompanyId && !portalLikeEdit
                    ? `Select ${coordLabels.companyLabel.toLowerCase()} first`
                    : clientsLoading
                      ? 'Loading clients…'
                      : clientOptions.length
                        ? `Select ${coordLabels.clientLabel.toLowerCase()}`
                        : 'No clients available'
                }
                required={requireClientSelection && !readOnly}
                disabled={readOnly || clientsLoading || (!externalCompanyId && !portalLikeEdit)}
                error={fieldErrors.externalCompanyClientId ? `${coordLabels.clientLabel} is required` : ''}
                size="md"
              />
              {!clientsLoading && clientOptions.length === 0 && (externalCompanyId || portalLikeEdit) && (
                <p className="mt-1 text-xs text-gray-500">
                  No clients available for this company.{' '}
                  <button
                    type="button"
                    onClick={() => setShowAddClientModal(true)}
                    className="text-indigo-600 font-medium hover:underline cursor-pointer"
                  >
                    Click here to add a new client
                  </button>
                  .
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderCoordinatorContactFields = () => (
    <>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-900 mb-1">
          {coordLabels.contactNameLabel || 'Name'} {!readOnly && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          value={submitterName}
          onChange={(e) => { if (!readOnly) { setSubmitterName(e.target.value); if (fieldErrors.submitterName) setFieldErrors((prev) => ({ ...prev, submitterName: false })); } }}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder={readOnly ? '' : (coordLabels.contactNameLabel || 'Name')}
          className={`w-full rounded border px-3 py-2 text-sm ${fieldErrors.submitterName ? 'border-red-500 bg-red-50' : 'border-gray-300'}${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`}
        />
        {fieldErrors.submitterName && <p className="mt-1 text-xs text-red-600">{(coordLabels.contactNameLabel || 'Name')} is required</p>}
      </div>
      <div>
        <PhoneInput
          label={coordLabels.contactMobileLabel || 'Contact Number'}
          name="submitterMobile"
          value={submitterMobile}
          onChange={(e) => { if (!readOnly) { setSubmitterMobile(e.target.value); if (fieldErrors.submitterMobile) setFieldErrors((prev) => ({ ...prev, submitterMobile: false })); } }}
          required={!readOnly}
          disabled={readOnly}
          defaultCountry="LK"
          placeholder={readOnly ? '' : `Enter ${(coordLabels.contactMobileLabel || 'Contact Number').toLowerCase()}`}
        />
        {fieldErrors.submitterMobile && <p className="mt-1 text-xs text-red-600">{(coordLabels.contactMobileLabel || 'Contact Number')} is required</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          {coordLabels.contactEmailLabel || 'Email'} {!readOnly && <span className="text-red-500">*</span>}
        </label>
        <input
          type="email"
          value={submitterEmail}
          onChange={(e) => { if (!readOnly) { setSubmitterEmail(e.target.value); if (fieldErrors.submitterEmail) setFieldErrors((prev) => ({ ...prev, submitterEmail: false })); } }}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder={readOnly ? '' : (coordLabels.contactEmailLabel || 'Email')}
          className={`w-full rounded border px-3 py-2 text-sm ${fieldErrors.submitterEmail ? 'border-red-500 bg-red-50' : 'border-gray-300'}${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`}
        />
        {fieldErrors.submitterEmail && <p className="mt-1 text-xs text-red-600">{(coordLabels.contactEmailLabel || 'Email')} is required</p>}
      </div>
    </>
  );

  const renderBrandProductFields = (opts = {}) => {
    const onlyProductName = Boolean(opts?.onlyProductName);
    const brandProductNameLabel = form?.settings?.brandProductLabel
      || form?.settings?.people?.labels?.brandProduct
      || (onlyProductName ? 'Brand / Name' : 'Brand / product');
    return (
      <>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-900 mb-1">
            {brandProductNameLabel} {(!readOnly && form?.settings?.requireBrandProduct) && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={brandProduct.brandsProducts}
            onChange={(e) => {
              if (!readOnly) {
                setBrandProduct((p) => ({ ...p, brandsProducts: e.target.value }));
                if (fieldErrors.brandsProducts) setFieldErrors((prev) => ({ ...prev, brandsProducts: false }));
              }
            }}
            readOnly={readOnly}
            disabled={readOnly}
            placeholder={readOnly ? '' : 'Brand or product this application represents'}
            maxLength={500}
            className={`w-full rounded border px-3 py-2 text-sm ${fieldErrors.brandsProducts ? 'border-red-500 bg-red-50' : 'border-gray-300'}${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`}
          />
          {!readOnly && fieldErrors.brandsProducts && <p className="mt-1 text-xs text-red-600">{brandProductNameLabel} is required</p>}
        </div>
        {!onlyProductName && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Brand category</label>
              <input
                type="text"
                value={brandProduct.brandCategory}
                onChange={(e) => { if (!readOnly) setBrandProduct((p) => ({ ...p, brandCategory: e.target.value })); }}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={readOnly ? '' : 'e.g. Beverages, Apparel'}
                maxLength={200}
                className={`w-full rounded border px-3 py-2 text-sm border-gray-300${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Brand website</label>
              <input
                type="text"
                value={brandProduct.brandWebsite}
                onChange={(e) => { if (!readOnly) setBrandProduct((p) => ({ ...p, brandWebsite: e.target.value })); }}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={readOnly ? '' : 'https://'}
                maxLength={255}
                className={`w-full rounded border px-3 py-2 text-sm border-gray-300${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`}
              />
            </div>
          </>
        )}
      </>
    );
  };

  const renderCategoryCard = () => (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="mb-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2">
        <h2 className="text-sm font-semibold tracking-wide text-indigo-900 uppercase">Category</h2>
        <p className="text-xs text-indigo-700 mt-0.5">
          {categoryOnlyIntake
            ? `Select the category you are entering. This applies to all ${peoplePluralLc} in this submission.`
            : `Select the industry and category you are entering. This applies to all ${peoplePluralLc} in this submission.`}
        </p>
      </div>
      <div className="space-y-3">
        {!categoryOnlyIntake && (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Industry {!categoryFieldsReadOnly && <span className="text-red-500">*</span>}
            </label>
            {categoryFieldsReadOnly ? (
              <div className="w-full rounded border px-3 py-2 text-sm bg-gray-50 border-gray-300 text-gray-700">
                {uniqueIndustries.find((i) => i.id === topLevelIndustryId)?.name || '—'}
              </div>
            ) : (
              <SearchableSelect
                value={topLevelIndustryId}
                onChange={(e) => {
                  setTopLevelIndustryId(e.target.value);
                  setTopLevelCategoryPath([]);
                  setTopLevelCategoryId('');
                  setFieldErrors((prev) => ({ ...prev, industryId: false, categoryId: false }));
                }}
                options={uniqueIndustries.map((ind) => ({ value: ind.id, label: ind.name }))}
                placeholder="Select industry"
                error={fieldErrors.industryId ? 'Industry is required' : ''}
                size="md"
              />
            )}
          </div>
        )}

        {!categoryOnlyIntake && topLevelIndustryId && activeRootCategories.length === 0 && (
          <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2.5">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-red-700">
              This industry has no eligible categories. Please choose a different industry.
            </p>
          </div>
        )}

        {(categoryOnlyIntake || topLevelIndustryId) && activeRootCategories.length > 0 && (() => {
          const levels = [];
          const rootCats = activeRootCategories;
          const hasSubcategories = rootCats.some((c) => c.hasEligibleChildren);
          const rootLabel = hasSubcategories ? 'Main Category' : 'Category';
          const rootPlaceholder = hasSubcategories ? 'Select main category' : 'Select category';
          levels.push(
            <div key="cat-level-0">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                {rootLabel} {!categoryFieldsReadOnly && <span className="text-red-500">*</span>}
              </label>
              {categoryFieldsReadOnly ? (
                <div className="w-full rounded border px-3 py-2 text-sm bg-gray-50 border-gray-300 text-gray-700">
                  {rootCats.find((c) => String(c.id) === topLevelCategoryPath[0])?.name || '—'}
                </div>
              ) : (
                <SearchableSelect
                  value={topLevelCategoryPath[0] || ''}
                  onChange={(e) => {
                    const newId = e.target.value;
                    const children = newId ? getChildCategoriesForParent(newId, allEligibilityCategories) : [];
                    setTopLevelCategoryPath(newId ? [newId] : []);
                    setTopLevelCategoryId(newId && !children.length ? newId : '');
                    setFieldErrors((prev) => ({ ...prev, categoryId: false }));
                  }}
                  options={rootCats.map((c) => ({ value: String(c.id), label: c.name }))}
                  placeholder={rootPlaceholder}
                  error={fieldErrors.categoryId && !topLevelCategoryPath[0] ? `${rootLabel} is required` : ''}
                  size="md"
                />
              )}
            </div>
          );
          for (let lvl = 0; lvl < topLevelCategoryPath.length; lvl++) {
            const selId = topLevelCategoryPath[lvl];
            if (!selId) break;
            const children = getChildCategoriesForParent(selId, allEligibilityCategories);
            if (!children.length) break;
            const capturedLvl = lvl + 1;
            levels.push(
              <div key={`cat-level-${capturedLvl}`}>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Sub-category {!categoryFieldsReadOnly && <span className="text-red-500">*</span>}
                </label>
                {categoryFieldsReadOnly ? (
                  <div className="w-full rounded border px-3 py-2 text-sm bg-gray-50 border-gray-300 text-gray-700">
                    {children.find((c) => String(c.id) === topLevelCategoryPath[capturedLvl])?.name || '—'}
                  </div>
                ) : (
                  <SearchableSelect
                    value={topLevelCategoryPath[capturedLvl] || ''}
                    onChange={(e) => {
                      const newId = e.target.value;
                      const grandChildren = newId ? getChildCategoriesForParent(newId, allEligibilityCategories) : [];
                      const newPath = newId
                        ? [...topLevelCategoryPath.slice(0, capturedLvl), newId]
                        : topLevelCategoryPath.slice(0, capturedLvl);
                      setTopLevelCategoryPath(newPath);
                      setTopLevelCategoryId(newId && !grandChildren.length ? newId : '');
                      setFieldErrors((prev) => ({ ...prev, categoryId: false }));
                    }}
                    options={children.map((c) => ({ value: String(c.id), label: c.name }))}
                    placeholder="Select sub-category"
                    error={fieldErrors.categoryId && topLevelCategoryPath.length === capturedLvl ? 'Sub-category is required' : ''}
                    size="md"
                  />
                )}
              </div>
            );
          }
          return <>{levels}</>;
        })()}

        {topLevelCategoryId && (() => {
          const cat = allEligibilityCategories.find((c) => String(c.id) === topLevelCategoryId);
          if (!cat) return null;
          const price = Number(cat.price || 0);
          return (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2.5 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-green-800">{cat.name}</p>
                {cat.pathBelowName && <p className="text-[11px] text-green-600 mt-0.5">{cat.pathBelowName}</p>}
                {cat.excludeFromVolumeDiscount ? (
                  <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                    Special Category · Volume discount not applicable
                  </span>
                ) : null}
              </div>
              {price > 0 && (
                <div className="text-right">
                  {pricingBreakdown.discountInfo?.type === 'BULK' ? (
                    <div>
                      <p className="text-sm font-bold text-green-800 whitespace-nowrap">
                        {fmtMoney(Math.max(0, price - Math.round(pricingBreakdown.discountAmount / Math.max(1, people.length))))} / {peopleSingularLc}
                      </p>
                      <p className="text-[11px] text-gray-400 line-through">
                        {fmtMoney(price)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm font-bold text-green-800 whitespace-nowrap">
                      {fmtMoney(price)} / {peopleSingularLc}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );

  const categorySectionHighlight = validationSections.includes('Category');
  const eventName = String(form?.event?.name || '').trim();
  const headerTitle = formatEventDisplayTitle(form?.event);
  const headerSubtitle = form?.name || 'Application Form';

  return (
    <div className="min-h-screen print:min-h-0 print:h-auto print:bg-white print:py-3 bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4">
      <UploadProgressOverlay
        visible={uploadOverlay.visible}
        files={uploadOverlay.files}
        overallPct={uploadOverlay.overallPct}
        phase={uploadOverlay.phase}
        errorMsg={uploadOverlay.errorMsg}
        onClose={() => setUploadOverlay({ visible: false, files: [], overallPct: 0, phase: 'uploading', errorMsg: '' })}
      />
      <div className="max-w-6xl mx-auto">
        <PublicSlimHeader
          title={headerTitle}
          subtitle={headerSubtitle}
          logoAlt="SLIM logo"
          logoClassName="h-11 w-auto object-contain"
          rightLogoSrc={eventLogoSrc}
          rightLogoAlt={eventName ? `${eventName} logo` : 'Event logo'}
          rightLogoClassName="h-14 w-14 rounded-full object-contain border border-amber-200 bg-white p-1"
        />
        {coordinatorPortal && (
          <div className="mb-3 text-right">
            <button type="button" onClick={handleBackToPortal} className="text-sm text-indigo-700 hover:underline">← Company portal</button>
          </div>
        )}
        {coordinatorPortal && !readOnly && !submitted && !isPreviewMode && !submissionId && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">
              {portalDraftMode ? 'You are editing a saved draft.' : 'Logged-in company application'}
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Use <strong>Save draft</strong> to continue later. You can return via the external portal before registration closes.
              Access is limited to your company&apos;s market (Local/International) and requires event applications to be enabled for your company.
            </p>
            {lastSavedAt && (
              <p className="text-xs text-amber-700 mt-1">
                Last saved: {new Date(lastSavedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
        {staffEdit && eventId && (
          <div className="mb-3 text-right">
            <button type="button" onClick={handleBackToSubmissions} className="text-sm text-indigo-700 hover:underline">← Event submissions</button>
          </div>
        )}
        {!readOnly && editPolicy?.message && (
          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            editPolicy?.pricingChangeRequiresProformaCancel
              ? 'border-violet-200 bg-violet-50 text-violet-950'
              : 'border-amber-200 bg-amber-50 text-amber-950'
          }`}>
            {editPolicy.message}
          </div>
        )}
        {staffView && eventId && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <button type="button" onClick={handleBackToSubmissions} className="text-sm text-indigo-700 hover:underline">← Event submissions</button>
            {(hasPermission('EVENTS:APPLICATIONFORMS:UPDATE') || isAdmin()) && submissionId ? (
              <Link
                to={`/events/${eventId}/submissions/${submissionId}/edit`}
                className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
              >
                Edit application
              </Link>
            ) : null}
          </div>
        )}
        {(readOnly || staffView) && submissionMeta && (
          <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-4">Application Summary</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Application Code</p>
                <p className="text-sm font-semibold text-gray-900 font-mono">{submissionMeta.applicationNumber || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Status</p>
                <p className="text-sm font-medium text-gray-900 capitalize">{submissionMeta.status ? submissionMeta.status.charAt(0) + submissionMeta.status.slice(1).toLowerCase() : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Submitted At</p>
                <p className="text-sm text-gray-900">{submissionMeta.submittedAt ? new Date(submissionMeta.submittedAt).toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Last Updated</p>
                <p className="text-sm text-gray-900">{submissionMeta.updatedAt ? new Date(submissionMeta.updatedAt).toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Industry</p>
                <p className="text-sm text-gray-900">{submissionMeta.industryName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Category</p>
                <p className="text-sm text-gray-900">{submissionMeta.categoryName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Form</p>
                <p className="text-sm text-gray-900">{submissionMeta.formName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Payment Reference</p>
                <p className="text-sm text-gray-900">{submissionMeta.paymentReferenceNumber || '—'}</p>
              </div>
            </div>
          </div>
        )}
        {(readOnly || staffView) && (
          <SubmissionSignedDocumentPanel
            submission={submissionMeta || {
              signedApplicationDocument: submissionMeta?.signedApplicationDocument,
              signedPdfPacks: submissionMeta?.signedPdfPacks,
              applicationForm: form,
            }}
            preferDownload={coordinatorPortal && readOnly}
            className="mb-5"
          />
        )}
        {staffView && submissionMeta && (submissionMeta.reviewNote || submissionMeta.reviewedByName) && (
          <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-2">Review Record</p>
            <p className="text-sm text-gray-700">
              {submissionMeta.reviewedByName ? (
                <span className="font-medium text-gray-900">{submissionMeta.reviewedByName}</span>
              ) : (
                'Reviewer'
              )}
              {submissionMeta.reviewedAt ? (
                <span>{` · ${new Date(submissionMeta.reviewedAt).toLocaleString()}`}</span>
              ) : null}
            </p>
            <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">{submissionMeta.reviewNote || 'No remarks.'}</p>
          </div>
        )}
        {coordinatorPortal && submissionId && token && (
          <SubmissionVersionHistory
            variant="portal"
            token={token}
            submissionId={submissionId}
            refreshKey={submissionMeta?.updatedAt}
            questionById={questionById}
          />
        )}
        {(staffEdit || staffView) && eventId && submissionId && (
          <SubmissionVersionHistory
            variant="staff"
            eventId={eventId}
            submissionId={submissionId}
            refreshKey={submissionMeta?.updatedAt}
            questionById={questionById}
          />
        )}
        <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0 bg-white rounded-xl shadow-lg overflow-hidden">
          <form
            onSubmit={onSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
            className="p-6 space-y-6"
          >
            {isPreviewMode && (
              <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2">
                <p className="text-sm font-semibold text-sky-900">Preview only</p>
                <p className="text-xs text-sky-800 mt-0.5">
                  This is a staff preview of the application form. Submissions are disabled — after approval, external companies apply via the portal (sign-in required).
                </p>
              </div>
            )}
            {validationSections.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                <div className="text-sm font-semibold text-amber-900">Please validate these sections:</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {validationSections.map((s) => (
                    <span key={s} className="inline-flex items-center rounded-full border border-amber-400 bg-white px-2.5 py-1 text-xs font-medium text-amber-900">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {paginationEnabled && formSteps?.length > 0 && (
              <FormPageStepper
                steps={formSteps}
                currentIndex={currentStepIndex}
                allowJump={allowStepJump}
                allowForwardJump={readOnly || isPreviewMode}
                onStepClick={allowStepJump ? handleStepClick : undefined}
              />
            )}

            {showSetupStep && leafEntryOptions.length === 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                No eligible entry categories are configured for this form. The organiser must map submission categories on the linked event.
              </p>
            )}

            {showSetupStep && (
              hasEntryDetailsPage ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="mb-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2">
                    <h2 className="text-sm font-semibold tracking-wide text-indigo-900 uppercase">
                      {coordLabels.sectionHeading || 'Cordinator Details'}
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {renderCoordinatorContactFields()}
                  </div>
                </div>
              ) : (
                <>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="mb-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2">
                      <h2 className="text-sm font-semibold tracking-wide text-indigo-900 uppercase">{coordLabels.sectionHeading}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {renderAgencyAndClientFields()}
                      {renderCoordinatorContactFields()}
                      {renderBrandProductFields()}
                    </div>
                  </div>
                  {leafEntryOptions.length > 0 && renderCategoryCard()}
                </>
              )
            )}

            {showQuestionSections && (
            <div className="border-t pt-4 space-y-4">
              {paginationEnabled && currentStep?.title && (
                <h2 className="text-base font-semibold text-gray-900">{currentStep.title}</h2>
              )}
              {paginationEnabled && currentStep?.description && (
                <p className="text-sm text-gray-600 leading-relaxed">{currentStep.description}</p>
              )}
              {(paginationEnabled ? sectionsToRender : orderedSections).map((section) => {
                const sectionKey = String(section?.key || '');
                const audience = sectionAudienceByKey.get(sectionKey) || 'NOMINEE';
                const sectionQuestions = questionsBySectionKey.get(sectionKey) || [];
                const visibleCommonQuestions = sectionQuestions.filter((q) => isQuestionVisible(q, commonVisibilityContext));

                if (audience === 'COMMON') {
                  const packKey = String(section?.pdfOutputKey || DEFAULT_PDF_PACK_KEY).trim() || DEFAULT_PDF_PACK_KEY;
                  const showPackLabel = multiPdfActive && packKey !== DEFAULT_PDF_PACK_KEY;
                  const isDuplicateTitle = Boolean(
                    currentStep?.title &&
                    section.title &&
                    currentStep.title.trim().toLowerCase() === section.title.trim().toLowerCase()
                  );
                  const showBanner = (section.title && !isDuplicateTitle) || section.description || showPackLabel;
                  return (
                    <div key={`sec-${sectionKey}`} className="border-t border-gray-200 pt-4">
                      {showBanner ? (
                        <div className="mb-3 rounded-lg border border-indigo-100 bg-gradient-to-r from-indigo-50 via-blue-50 to-white px-3 py-2.5 shadow-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            {section.title && !isDuplicateTitle ? (
                              <h2 className="text-sm font-semibold tracking-wide text-indigo-900">{section.title}</h2>
                            ) : null}
                            {showPackLabel ? (
                              <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                Separate PDF: {packTitleForKey(pdfOutputPacks, packKey)}
                              </span>
                            ) : null}
                          </div>
                          {section.description ? (
                            <p className="text-xs text-indigo-700/90 mt-1 leading-relaxed">{section.description}</p>
                          ) : null}
                        </div>
                      ) : null}
                      {visibleCommonQuestions.length === 0 ? (
                        <p className="text-xs text-gray-500">No questions in this section.</p>
                      ) : (
                        visibleCommonQuestions.map((q) =>
                          renderQuestionField(
                            q,
                            commonAnswers?.[answerKey(q)],
                            (nextVal) => { patchCommonAnswer(answerKey(q), nextVal); if (fieldErrors.commonAnswers?.[answerKey(q)]) setFieldErrors((prev) => ({ ...prev, commonAnswers: { ...prev.commonAnswers, [answerKey(q)]: false } })); },
                            `common_${sectionKey}_`,
                            Boolean(fieldErrors.commonAnswers?.[answerKey(q)]),
                            readOnly,
                            commonVisibilityContext
                          ))
                      )}
                    </div>
                  );
                }

                return (
                  <div key={`sec-${sectionKey}`} className="border-t border-gray-200 pt-4">
                    {(() => {
                      const packKey = String(section?.pdfOutputKey || DEFAULT_PDF_PACK_KEY).trim() || DEFAULT_PDF_PACK_KEY;
                      const showPackLabel = multiPdfActive && packKey !== DEFAULT_PDF_PACK_KEY;
                      const isDuplicateTitle = Boolean(
                        currentStep?.title &&
                        section.title &&
                        currentStep.title.trim().toLowerCase() === section.title.trim().toLowerCase()
                      );
                      const showBanner = (section.title && !isDuplicateTitle) || section.description || showPackLabel;
                      if (!showBanner) return null;
                      return (
                        <div className="mb-3 rounded-lg border border-purple-100 bg-gradient-to-r from-purple-50 via-indigo-50 to-white px-3 py-2.5 shadow-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            {section.title && !isDuplicateTitle ? (
                              <h2 className="text-sm font-semibold tracking-wide text-purple-900">{section.title}</h2>
                            ) : null}
                            {showPackLabel ? (
                              <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                Separate PDF: {packTitleForKey(pdfOutputPacks, packKey)}
                              </span>
                            ) : null}
                          </div>
                          {section.description ? (
                            <p className="text-xs text-purple-700/90 mt-1 leading-relaxed">{section.description}</p>
                          ) : null}
                        </div>
                      );
                    })()}

                    {!readOnly && sectionKey === firstNomineeSectionKey && (
                      <div className="hidden">
                        <button
                          type="button"
                          onClick={addPerson}
                          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-semibold rounded-lg shadow-md transition-all"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {peopleLabels.addButton}
                        </button>
                      </div>
                    )}

                    <div className="space-y-4">
                      {people.map((p, idx) => (
                        <div key={`person_${sectionKey}_${idx}`} className="rounded-lg border border-gray-200 p-4 bg-white">
                          {!readOnly && (
                            <div className="hidden">
                              <button
                                type="button"
                                onClick={() => removePerson(idx)}
                                className="inline-flex items-center px-3 py-1.5 bg-red-50 border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-100 transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Remove
                              </button>
                            </div>
                          )}

                          {sectionKey === firstNomineeSectionKey && (() => {
                            const rf = Array.isArray(form?.settings?.people?.requiredFields) ? form.settings.people.requiredFields : [];
                            const showFullName = true;
                            const showDesignation = !hasEntryDetailsPage && rf.includes('designation');
                            const showEmail = !hasEntryDetailsPage;
                            const showPhone = !hasEntryDetailsPage;
                            const pe = fieldErrors.people?.[idx] || {};
                            const clearPersonError = (field) => {
                              if (pe[field]) setFieldErrors((prev) => ({ ...prev, people: prev.people.map((x, i) => i === idx ? { ...x, [field]: false } : x) }));
                            };
                            return (
                              <div className="mb-3">
                                {(!hasEntryDetailsPage || people.length > 1) && (
                                  <div className="mb-3 rounded-md border border-purple-100 bg-purple-50 px-3 py-2">
                                    <h3 className="text-xs font-semibold tracking-wide text-purple-900">{personRowHeading(peopleLabels, idx, people.length)}</h3>
                                  </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {showFullName && (
                                    <div className={hasEntryDetailsPage ? 'md:col-span-2' : ''}>
                                      <label className="block text-sm font-medium text-gray-900 mb-1">
                                        {peopleLabels.fullName} {!readOnly && <span className="text-red-500">*</span>}
                                      </label>
                                      <input value={p.fullName || ''} onChange={(e) => { if (!readOnly) { patchPerson(idx, { fullName: e.target.value }); clearPersonError('fullName'); } }} readOnly={readOnly} disabled={readOnly} placeholder={readOnly ? '' : peopleLabels.fullName} className={`w-full rounded border px-3 py-2 text-sm ${pe.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'}${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`} />
                                      {!readOnly && pe.fullName && <p className="mt-1 text-xs text-red-600">{peopleLabels.fullName} is required</p>}
                                    </div>
                                  )}

                                  {hasEntryDetailsPage && idx === 0 && (
                                    <>
                                      {renderAgencyAndClientFields()}
                                      {renderBrandProductFields({ onlyProductName: true })}
                                    </>
                                  )}

                                  {showDesignation && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-900 mb-1">
                                        {peopleLabels.designation} {!readOnly && <span className="text-red-500">*</span>}
                                      </label>
                                      <input value={p.designation || ''} onChange={(e) => { if (!readOnly) { patchPerson(idx, { designation: e.target.value }); clearPersonError('designation'); } }} readOnly={readOnly} disabled={readOnly} placeholder={readOnly ? '' : peopleLabels.designation} className={`w-full rounded border px-3 py-2 text-sm ${pe.designation ? 'border-red-500 bg-red-50' : 'border-gray-300'}${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`} />
                                      {!readOnly && pe.designation && <p className="mt-1 text-xs text-red-600">{peopleLabels.designation} is required</p>}
                                    </div>
                                  )}
                                  {showEmail && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-900 mb-1">
                                        {peopleLabels.email} {!readOnly && <span className="text-red-500">*</span>}
                                      </label>
                                      <input type="email" value={p.email || ''} onChange={(e) => { if (!readOnly) { patchPerson(idx, { email: e.target.value }); clearPersonError('email'); } }} readOnly={readOnly} disabled={readOnly} placeholder={readOnly ? '' : peopleLabels.email} className={`w-full rounded border px-3 py-2 text-sm ${pe.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}${readOnly ? ' bg-gray-50 cursor-not-allowed' : ''}`} />
                                      {!readOnly && pe.email && <p className="mt-1 text-xs text-red-600">{peopleLabels.email} is required</p>}
                                    </div>
                                  )}
                                  {showPhone && (
                                    <div>
                                      <PhoneInput
                                        label={peopleLabels.phone}
                                        name={`person_${idx}_phone`}
                                        value={p.phone || ''}
                                        onChange={({ target: { value: v } }) => { if (!readOnly) { patchPerson(idx, { phone: v }); clearPersonError('phone'); } }}
                                        required={!readOnly}
                                        disabled={readOnly}
                                        defaultCountry="LK"
                                        placeholder={readOnly ? '' : `Enter ${peopleLabels.phone.toLowerCase()}`}
                                        error={pe.phone ? `${peopleLabels.phone} is required` : ''}
                                      />
                                    </div>
                                  )}
                                </div>

                                {hasEntryDetailsPage && idx === 0 && leafEntryOptions.length > 0 && (
                                  <div className="mt-4">
                                    {renderCategoryCard()}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {sectionKey !== firstNomineeSectionKey && (
                            <div className="mb-3 rounded-md border border-purple-100 bg-purple-50 px-3 py-2">
                              <h3 className="text-xs font-semibold tracking-wide text-purple-900">{personRowHeading(peopleLabels, idx, people.length)}</h3>
                              {p.fullName ? (
                                <p className="text-sm text-gray-700 mt-1">{p.fullName}</p>
                              ) : null}
                            </div>
                          )}

                          {sectionQuestions.filter((q) => isQuestionVisible(q, buildNomineeVisibilityContext(p))).length === 0 ? (
                            <p className="text-xs text-gray-500">No questions in this section.</p>
                          ) : (
                            sectionQuestions
                              .filter((q) => isQuestionVisible(q, buildNomineeVisibilityContext(p)))
                              .map((q) =>
                              renderQuestionField(
                                q,
                                p.answers?.[answerKey(q)],
                                (nextVal) => { patchPersonAnswer(idx, answerKey(q), nextVal); if (fieldErrors.people?.[idx]?.answers?.[answerKey(q)]) setFieldErrors((prev) => ({ ...prev, people: prev.people.map((pe, i) => i === idx ? { ...pe, answers: { ...pe.answers, [answerKey(q)]: false } } : pe) })); },
                                `person_${idx}_${sectionKey}_`,
                                Boolean(fieldErrors.people?.[idx]?.answers?.[answerKey(q)]),
                                readOnly,
                                buildNomineeVisibilityContext(p)
                              ))
                          )}

                          {/* Category-specific questions — rendered inside nominee section immediately after standard questions */}
                          {sectionKey === categoryQuestionsSectionKey && activeCategoryQuestions.length > 0 && (
                            <>
                              <div className="border-t border-gray-100 my-3" />
                              <div className="mb-3 rounded-md border border-teal-100 bg-teal-50 px-3 py-2">
                                <h4 className="text-xs font-semibold tracking-wide text-teal-900">
                                  {[activeCategoryQuestionSet?.industryName, activeCategoryQuestionSet?.categoryName].filter(Boolean).join(' — ')} — Category Questions
                                </h4>
                              </div>
                              {activeCategoryQuestions
                                .filter((q) => isQuestionVisible(q, buildNomineeVisibilityContext(p)))
                                .map((q) => {
                                const ak = answerKey(q);
                                return renderQuestionField(
                                  q,
                                  p.answers?.[ak],
                                  (nextVal) => {
                                    patchPersonAnswer(idx, ak, nextVal);
                                    if (fieldErrors.people?.[idx]?.answers?.[ak]) {
                                      setFieldErrors((prev) => ({
                                        ...prev,
                                        people: prev.people.map((pe, i) => i === idx ? { ...pe, answers: { ...pe.answers, [ak]: false } } : pe),
                                      }));
                                    }
                                  },
                                  `catq_person_${idx}_`,
                                  Boolean(fieldErrors.people?.[idx]?.answers?.[ak]),
                                  readOnly,
                                  buildNomineeVisibilityContext(p)
                                );
                              })}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

            </div>
            )}

            {(!paginationEnabled || isLastStep) && showLegacyIntentChoice && (
              <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                <p className="text-sm font-semibold text-slate-900">Application type</p>
                <p className="text-xs text-slate-600">
                  Choose whether you are resubmitting a previous offline application or submitting a new one.
                </p>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="legacySubmissionIntent"
                      value="PREVIOUSLY_SUBMITTED"
                      checked={legacySubmissionIntent === 'PREVIOUSLY_SUBMITTED'}
                      onChange={() => setLegacySubmissionIntent('PREVIOUSLY_SUBMITTED')}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-800">
                      <strong>Previously submitted offline</strong> — resubmitting an application you already sent before.
                      Staff will process this through legacy onboarding without automatic confirmation emails.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="legacySubmissionIntent"
                      value="FRESH"
                      checked={legacySubmissionIntent === 'FRESH'}
                      onChange={() => setLegacySubmissionIntent('FRESH')}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-800">
                      <strong>New application</strong> — a fresh submission following the normal flow including emails and proforma steps.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {(!paginationEnabled || isLastStep) && !readOnly && !isPreviewMode && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-amber-900 mb-1.5">Important – Please Read Before Submitting</p>
                    <ul className="text-xs text-amber-800 space-y-1.5 list-disc list-inside">
                      {legacySubmissionIntent === 'PREVIOUSLY_SUBMITTED' ? (
                        <>
                          {requiresSignedDocUpload && (
                            <li>Upon submission, a <strong>PDF copy of this application</strong> will be automatically downloaded. Sign this PDF with <strong>relevant authorised signatures and the company seal</strong>, then upload it from the External portal when required.</li>
                          )}
                          <li><strong>Previously submitted offline:</strong> SLIM staff will process billing through <strong>legacy onboarding</strong>. You do <strong>not</strong> need to request a proforma invoice from the external portal.</li>
                        </>
                      ) : (
                        <>
                          {requiresSignedDocUpload && (
                            <li>Upon submission, a <strong>PDF copy of this application</strong> will be automatically downloaded and also emailed to you. Sign this PDF with <strong>relevant authorised signatures and the company seal</strong>, then upload it from the External portal at any time while the application is editable.</li>
                          )}
                          <li>Once you have submitted all your {peoplePluralLc}, please <strong>log in to the system</strong> and request a <strong>proforma invoice</strong> for the applications.</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {(!paginationEnabled || isLastStep) && !readOnly && !isPreviewMode && requireAgreement && (
              <div className={`mt-4 rounded-lg border px-4 py-3 ${agreementError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreementChecked}
                    onChange={(e) => { setAgreementChecked(e.target.checked); if (e.target.checked) setAgreementError(false); }}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 accent-indigo-600"
                  />
                  <span className={`text-sm ${agreementError ? 'text-red-700' : 'text-gray-700'}`}>
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowAgreementModal(true)}
                      className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2 font-medium"
                    >
                      Terms &amp; Conditions
                    </button>
                  </span>
                </label>
                {agreementError && (
                  <p className="mt-1.5 text-xs text-red-600">You must accept the Terms &amp; Conditions to continue.</p>
                )}
              </div>
            )}

            {paginationEnabled && !readOnly && !isPreviewMode && (
              <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 mt-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    disabled={currentStepIndex === 0}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {coordinatorPortal && !submissionId && (
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={draftSaving || submitting}
                      className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {draftSaving ? 'Saving draft…' : 'Save draft'}
                    </button>
                  )}
                </div>
                {!isLastStep ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={isStepChanging}
                    className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting || draftSaving || isStepChanging || (requireAgreement && !agreementChecked)}
                    className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium disabled:opacity-60"
                  >
                    {submitting ? (submissionId ? 'Saving…' : 'Submitting application…') : (submissionId ? 'Save changes' : 'Submit Application')}
                  </button>
                )}
              </div>
            )}

            {paginationEnabled && (readOnly || isPreviewMode) && (
              <div className="pt-4 flex items-center justify-between gap-3 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  disabled={currentStepIndex === 0}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={isLastStep}
                  className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}

            {!paginationEnabled && !readOnly && !isPreviewMode && (
              <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 border-t border-gray-100 mt-4">
                {coordinatorPortal && !submissionId && (
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={draftSaving || submitting}
                    className="w-full sm:w-auto px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                  >
                    {draftSaving ? 'Saving draft…' : 'Save draft'}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting || draftSaving || (requireAgreement && !agreementChecked)}
                  className="w-full sm:w-auto px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium disabled:opacity-60"
                >
                  {submitting ? (submissionId ? 'Saving…' : 'Submitting application…') : (submissionId ? 'Save changes' : 'Submit Application')}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Pricing sidebar */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-6">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
              <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
              </svg>
              <h3 className="text-sm font-bold text-gray-900">Entry Fee Summary</h3>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 ml-auto">{marketLabel(pricingMarket)}</span>
            </div>
            {pricingBreakdown.lines.length === 0 ? (
              <p className="text-xs text-gray-500 leading-relaxed">Select your entry category above to see the cost breakdown.</p>
            ) : (
              <div className="space-y-3">
                {pricingBreakdown.lines.map((line) => (
                  <div key={line.id} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800 leading-snug truncate" title={line.name}>{line.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {line.count} {line.count === 1 ? peopleSingularLc : peoplePluralLc} × {fmtMoney(line.price)}
                      </div>
                    </div>
                    <div className="font-semibold text-gray-900 whitespace-nowrap flex-shrink-0">
                      {fmtMoney(line.subtotal)}
                    </div>
                  </div>
                ))}

                <div className="border-t border-gray-100 pt-2 flex justify-between items-center text-sm text-gray-600">
                  <span>Entry Fee Subtotal</span>
                  <span>{fmtMoney(pricingBreakdown.entryFeeTotal)}</span>
                </div>

                {pricingBreakdown.isCategoryVolumeExempt && form?.event?.bulkDiscountsEnabled && (
                  <div className="text-2xs text-amber-800 bg-amber-50 p-2 rounded border border-amber-200">
                    Volume discount is not applicable for this special category.
                  </div>
                )}

                {pricingBreakdown.discountInfo && pricingBreakdown.discountAmount > 0 && (
                  <div className="flex items-start justify-between gap-2 text-sm text-emerald-700 bg-emerald-50/70 p-2.5 rounded-lg border border-emerald-100">
                    <div className="min-w-0">
                      <div className="font-semibold text-emerald-800 text-xs flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span className="truncate">{pricingBreakdown.discountInfo.label}</span>
                      </div>
                      <div className="text-2xs text-emerald-600 mt-0.5">
                        {pricingBreakdown.discountTaxCalculationBasis === 'POST_TAX' ? 'Applied on total after tax' : 'Applied on category price'}
                        {pricingBreakdown.discountInfo.discountType === 'PERCENTAGE' ? ` (${pricingBreakdown.discountInfo.discountValue}%)` : ''}
                      </div>
                    </div>
                    <div className="font-bold text-emerald-700 whitespace-nowrap flex-shrink-0">
                      -{fmtMoney(pricingBreakdown.discountAmount)}
                    </div>
                  </div>
                )}

                {pricingBreakdown.taxLines.length > 0 && (
                  pricingBreakdown.taxLines.map((tax) => (
                    <div key={tax.name} className="flex justify-between items-center text-sm text-gray-600">
                      <span>{tax.name}{tax.code ? ` (${tax.code})` : ''} <span className="text-xs text-gray-400">{tax.percentage}%</span></span>
                      <span>{fmtMoney(tax.amount)}</span>
                    </div>
                  ))
                )}

                <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-900">Total {pricingBreakdown.taxLines.length > 0 ? '(incl. taxes)' : ''}</span>
                  <span className="text-base font-bold text-indigo-700">{fmtMoney(pricingBreakdown.grandTotal)}</span>
                </div>

                {/* Promo Code Input Section */}
                {!readOnly && !submissionId && (
                  <div className="border-t border-gray-100 pt-3 mt-1">
                    {appliedPromo ? (
                      <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-2.5 py-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-xs font-bold text-purple-800 tracking-wider font-mono">{appliedPromo.code}</span>
                          <span className="text-2xs text-purple-600 font-medium">Applied</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemovePromoCode}
                          className="text-xs text-rose-600 hover:text-rose-800 font-medium ml-2 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-2xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                          Promo Code
                        </label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={promoCodeInput}
                            onChange={(e) => {
                              setPromoCodeInput(e.target.value.toUpperCase());
                              if (promoError) setPromoError('');
                            }}
                            placeholder="Enter promo code"
                            className="w-full text-xs font-mono uppercase px-2.5 py-1.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={promoLoading}
                          />
                          <button
                            type="button"
                            onClick={handleApplyPromoCode}
                            disabled={promoLoading || !promoCodeInput.trim()}
                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md transition-colors"
                          >
                            {promoLoading ? '…' : 'Apply'}
                          </button>
                        </div>
                        {promoError && (
                          <p className="text-2xs text-rose-600 mt-1">{promoError}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        </div>
      </div>

      {showAgreementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Terms &amp; Conditions</h2>
              <button
                type="button"
                onClick={() => setShowAgreementModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed flex-1">
              {agreementText}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setShowAgreementModal(false)}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={proformaCancelPromptOpen}
        onClose={() => !submitting && setProformaCancelPromptOpen(false)}
        onConfirm={async () => {
          setProformaCancelPromptOpen(false);
          await executeSubmission(true);
        }}
        title="Cancel proforma request?"
        message={getProformaCancelEditMessage()}
        confirmText={submitting ? 'Saving…' : 'Save and cancel proforma'}
        type="danger"
        loading={submitting}
      />
      <ConfirmationModal
        isOpen={financeAckPromptOpen}
        onClose={() => !submitting && setFinanceAckPromptOpen(false)}
        onConfirm={async () => {
          setFinanceAckPromptOpen(false);
          await executeSubmission(false, true);
        }}
        title="Confirm category / fee correction?"
        message="Pricing will be recalculated from the new category. If a tax invoice is already issued, invoice amounts and invoice/payment GL will be reverse-reposted to match; otherwise an unpaid fee increase still uses an additional proforma. Schedule seats that no longer match will be cleared so Ops can reseat the applicant."
        confirmText={submitting ? 'Saving…' : 'Save correction'}
        type="warning"
        loading={submitting}
      />
      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        companyId={externalCompanyId}
        companyName={externalCompanyLabel}
        defaultCompanyName={cleanCompanyName(externalCompanyLabel)}
        token={token || form?.publicToken}
        coordinatorPortal={coordinatorPortal}
        onSuccess={async (newClient) => {
          if (newClient?._id) {
            await refreshClientOptions(externalCompanyId, newClient._id, newClient);
          }
        }}
      />
    </div>
  );
};

export default PublicApplicationForm;
